import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation, useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { initials } from "../utils/initials";
import { useProject } from "../contexts/ProjectContext";
import { useAppShell } from "../contexts/AppShellContext";
import { authFetch } from "../utils/authFetch";
import ProgramsSettings from "./ProgramsSettings";
import { usePartnerDocAccess, type AccessRule } from "../hooks/useDocAccess";
import AccountAccessPanel from "./AccountAccessPanel";
import { ExternalUsersSection } from "./ExternalUsersSection";
import { ExternalUserDetailInline } from "./ExternalUserDetail";
import HeaderRow1 from "./shell/HeaderRow1";
import SettingsPage from "../pages/SettingsPage";
import CmsAdminPage from "../pages/CmsAdminPage";
import MergeRequestDashboardPage from "../pages/MergeRequestDashboardPage";
import BranchDiffPage from "../pages/BranchDiffPage";
import CommitGraphVisualizer from "./docs/CommitGraphVisualizer";
import ProjectSharingTab from "./ProjectSharingTab";
import ProjectDefaultAccessTab from "./ProjectDefaultAccessTab";
import WebhooksPanel from "./WebhooksPanel";
import ReaderQuestionsTab from "./ReaderQuestionsTab";
import PaginationBar from "./PaginationBar";
import ApiAccessPanel from "./ApiAccessPanel";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  Palette, Users, FolderOpen, Building2,
  GitBranch, History, UserPlus, Trash2,
  ChevronRight, ChevronDown, Plus, Minus, AlertCircle, X, Settings2,
  Loader2, Save, RotateCcw, Folder, FileText, Code, Check, Search, ArrowLeft, Network, Package,
  GitPullRequest, GitCompare, Link2, LifeBuoy, HelpCircle,
  Landmark, KeyRound, ShieldCheck,
  MessageCircleQuestion,
} from "lucide-react";
import { AUDIENCE_CHOICES, AudienceChip, JoveoChip, audienceOf, canChangeAudience, canChangeAudienceTo, supportsPartnerAccess, describePartnerReach, type Audience, type JoveoRole, type PartnerReach } from "./audience";

const SIDEBAR_W = 280;
const BASE = "/studio/settings";

type ProjectTab = "reader-questions" | "general" | "external-view" | "branches" | "commits" | "builds" | "graph" | "merge-requests" | "compare" | "members" | "share-links";

interface NavItem { key: string; label: string; icon: typeof Palette; hint: string; }
interface NavGroup { label?: string; items: NavItem[]; }

export default function SettingsLayout() {
  const { user } = useAuth();
  const { entitledApps, switchApp } = useAppShell();
  const { currentProject, refreshProjects } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  // Derive active section from URL path
  const rel = location.pathname.replace(BASE, "").replace(/^\//, "");
  const activeKey = (() => {
    if (rel.startsWith("programs")) return "programs";
    if (rel.startsWith("partners")) return "partners";
    if (rel.startsWith("external-users")) return "external-users";
    if (rel.startsWith("api-access")) return "api-access";
    if (rel.startsWith("projects")) return "projects";
    if (rel.startsWith("users")) return "users";
    if (rel.startsWith("default-config")) return "default-config";
    if (rel.startsWith("theme")) return "theme";
    if (rel === "") return "home";
    return "home";
  })();

  // The sections this person can open, grouped as in the sidebar menu. The
  // settings home lists the same ones, so "← Settings" always has a page to
  // land on (redirecting the home to Projects hid the menu).
  const projectsItem: NavItem = { key: "projects", label: "Projects", icon: FolderOpen, hint: "Each project's access, members, share links, branches and history." };
  const navGroups: NavGroup[] = isSuperAdmin ? [
    { label: "Settings", items: [
      projectsItem,
      { key: "users", label: "Users", icon: Users, hint: "Everyone who can sign in, and who is a super admin." },
      { key: "theme", label: "Theme", icon: Palette, hint: "Fonts, sizes and spacing of the docs pages." },
    ] },
    { label: "External Access", items: [
      { key: "partners", label: "Partners", icon: Building2, hint: "Partner firms, their people and the docs they can read." },
      { key: "programs", label: "Programs", icon: Landmark, hint: "White-label programs and their Enable consoles." },
      { key: "external-users", label: "External Users", icon: UserPlus, hint: "People outside Joveo and their invites." },
      { key: "api-access", label: "API Access", icon: KeyRound, hint: "Service accounts that let other products read the docs." },
    ] },
  ] : [
    { items: [projectsItem] },
  ];
  // With a single section there is no menu to pick from: Settings opens it
  // (on the current project) and Back leaves Settings.
  const onlySection = navGroups.flatMap(g => g.items).length === 1 ? navGroups[0].items[0].key : null;
  useEffect(() => {
    if (!onlySection || rel !== "") return;
    navigate(onlySection === "projects" && currentProject
      ? `${BASE}/projects/${encodeProjectSlug(currentProject.slug)}`
      : `${BASE}/${onlySection}`, { replace: true });
  }, [onlySection, currentProject, rel, navigate]);

  // Detect if we're inside a project detail
  const relParts = rel.split("/");
  const isInProject = relParts[0] === "projects" && !!relParts[1];
  const projectEncodedSlug = isInProject ? relParts[1] : null;

  // Detect if we're inside a partner detail
  const isInPartner = relParts[0] === "partners" && !!relParts[1];
  const partnerSlugFromUrl = isInPartner ? relParts[1] : null;

  // Partners list for sidebar (loaded when on partners section)
  const [sidebarPartners, setSidebarPartners] = useState<Array<{ id: string; slug: string; name: string; managedByProgram?: { slug: string; name: string } | null }>>([]);
  const [showProgramManaged, setShowProgramManaged] = useState(false);
  const [partnersLoaded, setPartnersLoaded] = useState(false);
  useEffect(() => {
    if (activeKey === "partners") {
      (async () => {
        const r = await authFetch("/api/admin/partners");
        if (r.ok) setSidebarPartners(await r.json());
        setPartnersLoaded(true);
      })();
    }
  }, [activeKey]);

  // Projects list for sidebar (loaded when on projects section)
  const [sidebarProjects, setSidebarProjects] = useState<Array<AdminProject & { memberCount: number; joveoRole?: JoveoRole | null }>>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const loadSidebarProjects = useCallback(async () => {
    const r = await authFetch("/api/admin/projects");
    if (r.ok) setSidebarProjects(await r.json());
    setProjectsLoaded(true);
  }, []);
  // Reload projects whenever we're on the projects tab or when the path changes
  // (handles navigating away and back, or creating/modifying projects)
  useEffect(() => {
    if (activeKey === "projects") { loadSidebarProjects(); }
  }, [activeKey, location.pathname, loadSidebarProjects]);


  // Auto-redirect to first project when on projects section but none selected
  useEffect(() => {
    if (activeKey === "projects" && projectsLoaded && sidebarProjects.length > 0 && !isInProject) {
      navigate(`${BASE}/projects/${encodeProjectSlug(sidebarProjects[0].slug)}?tab=general`, { replace: true });
    }
  }, [activeKey, projectsLoaded, sidebarProjects, isInProject, navigate]);

  const item = (i: NavItem) => {
    const on = activeKey === i.key;
    return (
      <button key={i.key} onClick={() => navigate(`${BASE}/${i.key}`, { replace: true })}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${on ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}>
        <i.icon className="size-4" strokeWidth={1.75} />{i.label}
      </button>
    );
  };

  // Show create project modal from sidebar + button
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projectVisFilter, setProjectVisFilter] = useState<Audience>("private");

  // Get active project tab from URL search params
  const [searchParams] = useSearchParams();
  const projectTab = (searchParams.get("tab") as ProjectTab) || "general";
  const partnerTab = (searchParams.get("tab") as "members" | "access" | "products" | "apps") || "members";

  // Four audience groups, not two. The old split was on the raw `visibility`
  // enum, which cannot tell a partner project from a public one — both are
  // 'external'.
  const byAudience = (a: Audience) => sidebarProjects.filter(p => audienceOf(p) === a);
  const AUDIENCE_ORDER: Audience[] = ["private", "shared", "partner", "public"];

  // Auto-select the tab that has projects on initial load (when not viewing a specific project)
  const initialTabSet = useRef(false);
  useEffect(() => {
    if (!projectsLoaded || initialTabSet.current || isInProject) return;
    initialTabSet.current = true;
    const firstNonEmpty = AUDIENCE_ORDER.find(a => byAudience(a).length > 0);
    if (firstNonEmpty && byAudience(projectVisFilter).length === 0) {
      setProjectVisFilter(firstNonEmpty);
    }
  }, [projectsLoaded, isInProject, sidebarProjects]);

  // Sync projectVisFilter with the project currently shown in the URL
  useEffect(() => {
    if (!projectsLoaded || !isInProject || !projectEncodedSlug) return;
    const decodedSlug = decodeProjectSlug(projectEncodedSlug);
    const proj = sidebarProjects.find(p => p.slug === decodedSlug);
    if (proj) {
      setProjectVisFilter(audienceOf(proj));
    }
  }, [projectsLoaded, isInProject, projectEncodedSlug, sidebarProjects]);

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <HeaderRow1
        currentApp={null}
        entitledApps={entitledApps}
        onSwitchApp={switchApp}
      />
      <div className="flex flex-1 overflow-hidden">
        <aside className="shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-hidden" style={{ width: SIDEBAR_W }}>
          <nav className="flex-1 px-3 pt-3 space-y-3 overflow-y-auto">
            {/* Top actions */}
            <div className="flex items-center gap-1">
              <button onClick={() => activeKey === "home" || onlySection ? navigate("/studio") : navigate(BASE, { replace: true })}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors">
                <ArrowLeft className="size-3.5" strokeWidth={1.75} />
                {activeKey !== "home" && !onlySection ? "Settings" : "Back"}
              </button>
            </div>
            <div className="border-t border-slate-100" />

            {activeKey === "projects" ? (
              /* ── Projects sidebar ── */
              <>
                <div className="flex items-center justify-between px-2 pb-2 mb-1 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="size-4 text-slate-500" strokeWidth={1.75} />
                    <span className="text-[14px] font-semibold text-slate-800">Projects</span>
                  </div>
                  {/* Any internal user may create one — the API gate is
                      _require_internal, and the audience fields are gated
                      separately both server-side and in AUDIENCE_CHOICES.
                      ProjectsEmptyState's own button was already ungated, so
                      a user with zero projects saw a working button and a
                      user with projects saw none. */}
                  {(
                    <button onClick={() => setShowCreateProject(true)}
                      className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" title="New Project">
                      <Plus className="size-3.5" strokeWidth={2} />
                    </button>
                  )}
                </div>
                {!projectsLoaded ? (
                  <div className="px-3 py-2 text-xs text-slate-400">Loading…</div>
                ) : sidebarProjects.length === 0 ? (
                  <div className="px-3 py-6 text-center">
                    <FolderOpen className="size-5 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-400 mb-2">No projects yet</p>
                    <button onClick={() => setShowCreateProject(true)} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">Create one →</button>
                  </div>
                ) : (
                  <>
                    {sidebarProjects.length > 0 && (
                      <div className="mx-1 mb-2 flex rounded-md bg-slate-100 p-0.5">
                        {AUDIENCE_ORDER.map(v => {
                          const list = byAudience(v);
                          return (
                            <button key={v} disabled={list.length === 0} onClick={() => {
                              setProjectVisFilter(v);
                              if (list.length > 0) {
                                navigate(`${BASE}/projects/${encodeProjectSlug(list[0].slug)}?tab=${projectTab}`, { replace: true });
                              }
                            }}
                              title={list.length === 0 ? `No ${v} projects` : undefined}
                              className={`flex-1 text-[11px] font-medium py-1 rounded transition-colors ${projectVisFilter === v ? "bg-white text-slate-700 shadow-sm" : list.length === 0 ? "text-slate-300 cursor-default" : "text-slate-400 hover:text-slate-600"}`}>
                              {v[0].toUpperCase() + v.slice(1)}
                              {list.length > 0 && <span className="ml-1 text-slate-300">{list.length}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="space-y-0.5 pl-3 ml-2 border-l border-slate-200 mt-1">
                      {byAudience(projectVisFilter).map(p => {
                        const selected = projectEncodedSlug === encodeProjectSlug(p.slug);
                        return (
                          <button key={p.id} onClick={() => navigate(`${BASE}/projects/${encodeProjectSlug(p.slug)}?tab=${projectTab}`, { replace: true })}
                            className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors truncate ${selected ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-800 font-medium"}`}>
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            ) : activeKey === "partners" ? (
              /* ── Partners sidebar: list of partners + detail tabs ── */
              <>
                <div className="flex items-center justify-between px-2 pb-2 mb-1 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Building2 className="size-4 text-slate-500" strokeWidth={1.75} />
                    <span className="text-[14px] font-semibold text-slate-800">Partners</span>
                  </div>
                  <button onClick={() => navigate(`${BASE}/partners?create=1`, { replace: true })}
                    className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" title="New Partner">
                    <Plus className="size-3.5" strokeWidth={2} />
                  </button>
                </div>
                <div className="space-y-0.5 pl-3 ml-2 border-l border-slate-200 mt-1">
                  {!partnersLoaded ? (
                    <div className="px-3 py-2 text-xs text-slate-400">Loading…</div>
                  ) : sidebarPartners.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-400">No partners yet</div>
                  ) : (() => {
                    // Program-managed partners (created via Enable's add-partner
                    // flow, potentially hundreds) collapse into one group so the
                    // handful of directly-managed partners stays scannable.
                    const direct = sidebarPartners.filter(p => !p.managedByProgram);
                    const programManaged = sidebarPartners.filter(p => !!p.managedByProgram);
                    const renderRow = (p: typeof sidebarPartners[number]) => {
                      const selected = partnerSlugFromUrl === p.slug;
                      return (
                        <button key={p.id} onClick={() => navigate(`${BASE}/partners/${p.slug}?tab=${partnerTab}`, { replace: true })}
                          className={`w-full text-left px-3 py-2 rounded-lg text-[13px] font-medium transition-colors truncate ${selected ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}>
                          <span className="truncate">{p.name}</span>
                          {p.managedByProgram && (
                            <span className="block text-[10px] font-normal text-slate-400 truncate">{p.managedByProgram.name}</span>
                          )}
                        </button>
                      );
                    };
                    const selectedIsHidden = !showProgramManaged
                      && programManaged.some(p => p.slug === partnerSlugFromUrl);
                    return (
                      <>
                        {direct.map(renderRow)}
                        {programManaged.length > 0 && (
                          <>
                            <button onClick={() => setShowProgramManaged(v => !v)}
                              className="w-full text-left px-3 py-2 rounded-lg text-[12px] font-medium text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
                              {showProgramManaged ? "▾" : "▸"} Program-managed ({programManaged.length})
                            </button>
                            {(showProgramManaged || selectedIsHidden) && programManaged.map(renderRow)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
                {/* Sub-tabs rendered in content area, not sidebar */}
              </>
            ) : (
              navGroups.map((g, gi) => (
                <div key={g.label ?? gi}>
                  {g.label && <div className="px-3 mb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{g.label}</div>}
                  <div className={`space-y-0.5${gi > 0 ? " pl-1.5" : ""}`}>{g.items.map(item)}</div>
                </div>
              ))
            )}
          </nav>

        </aside>
        <div className="flex-1 overflow-y-auto bg-slate-50/40">
          <div className="p-6">
            {(() => {
              const parts = rel.split("/");
              if (parts[0] === "projects" && parts[1]) return <ProjectDetailInline encodedSlug={parts[1]} onProjectUpdated={() => { loadSidebarProjects(); refreshProjects(); }} />;
              // Super-admin-only sections — redirect non-super-admins to projects
              if (!isSuperAdmin) {
                if (["users", "partners", "programs", "external-users", "api-access", "default-config", "theme"].includes(parts[0])) {
                  return <ProjectsEmptyState projects={sidebarProjects} loaded={projectsLoaded} onCreateClick={() => setShowCreateProject(true)} />;
                }
              }
              if (parts[0] === "partners" && parts[1]) return <PartnerDetailContent partnerSlug={parts[1]} partnerTab={partnerTab} sidebarPartners={sidebarPartners} onPartnersChanged={() => authFetch("/api/admin/partners").then(r => r.ok ? r.json() : []).then(setSidebarPartners)} />;
              if (parts[0] === "external-users" && parts[1]) return <ExternalUserDetailInline userId={parts[1]} />;
              if (activeKey === "users") return <AppUsers />;
              if (activeKey === "partners") return <PartnersEmptyState partners={sidebarPartners} loaded={partnersLoaded} onPartnersChanged={() => authFetch("/api/admin/partners").then(r => r.ok ? r.json() : []).then(setSidebarPartners)} />;
              if (activeKey === "programs") return <ProgramsSettings />;
              if (activeKey === "external-users") return <ExternalUsersSection />;
              if (activeKey === "api-access") return <ApiAccessPanel />;
              if (activeKey === "default-config") return <DefaultAccessMoved />;
              if (activeKey === "theme") return <SettingsPage />;
              if (activeKey === "projects") return <ProjectsEmptyState projects={sidebarProjects} loaded={projectsLoaded} onCreateClick={() => setShowCreateProject(true)} />;
              return onlySection ? null : <SettingsHome groups={navGroups} />;
            })()}
            {/* Create project modal — triggered from sidebar + button */}
            {showCreateProject && <CreateProjectModal onClose={() => setShowCreateProject(false)} onCreated={(slug) => { setShowCreateProject(false); loadSidebarProjects(); refreshProjects(); navigate(`${BASE}/projects/${encodeProjectSlug(slug)}?tab=general`, { replace: true }); }} />}
          </div>
        </div>
      </div>
    </div>
  );
}




/* ── Settings home: the sections this person can open ── */
function SettingsHome({ groups }: { groups: NavGroup[] }) {
  return (
    <div className="max-w-4xl space-y-6">
      <h1 className="text-[17px] font-semibold text-slate-800 leading-tight">Settings</h1>
      {groups.map((g, gi) => (
        <section key={g.label ?? gi} aria-label={g.label}>
          {g.label && <h2 className="mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{g.label}</h2>}
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
            {g.items.map(it => (
              <Link key={it.key} to={`${BASE}/${it.key}`} replace
                className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors">
                <it.icon className="size-5 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.75} />
                <span>
                  <span className="block text-[13px] font-semibold text-slate-800">{it.label}</span>
                  <span className="block text-[12px] text-slate-500 mt-0.5 leading-snug">{it.hint}</span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}


/* ── Projects Empty State — shown when no projects exist ── */
function ProjectsEmptyState({ projects, loaded, onCreateClick }: {
  projects: Array<{ id: string; name: string; slug: string; visibility: string }>;
  loaded: boolean;
  onCreateClick: () => void;
}) {
  if (projects.length > 0) return null;
  if (!loaded) return null;

  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="size-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
        <FolderOpen className="size-7 text-indigo-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-700 mb-1">No projects yet</h3>
      <p className="text-xs text-slate-400 mb-4 text-center max-w-xs">Create your first project to start managing documentation and content.</p>
      <button onClick={onCreateClick}
        className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors">
        <Plus className="size-3.5" /> Create Project
      </button>
    </div>
  );
}

/* The four names as presets over the two stored axes. "Product" is the one
   that is not an audience at all — it is a Joveo-grant of editor on a project
   that is not private, which is why it sets internalRole rather than any
   external-axis field. */
const PRESETS: Array<{
  key: string; title: string; hint: string;
  visibility: "internal" | "external";
  internalRole: "none" | "viewer" | "editor" | "admin";
  shareByDefault: boolean;
  needsPartner?: boolean;
}> = [
  { key: "private", title: "Private team docs",
    hint: "Only people you add. Never leaves Joveo.",
    visibility: "internal", internalRole: "none", shareByDefault: false },
  { key: "product", title: "Product documentation",
    hint: "All of Joveo edits it; share outside by naming people.",
    visibility: "external", internalRole: "editor", shareByDefault: false },
  { key: "partner", title: "Partner docs",
    hint: "For one partner. Pick them below.",
    visibility: "external", internalRole: "editor", shareByDefault: false,
    needsPartner: true },
  { key: "public", title: "Public docs",
    hint: "Anyone with Cortex access. Super admin only.",
    visibility: "external", internalRole: "editor", shareByDefault: true },
];

/* ── Create Project Modal ── */
function CreateProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (slug: string) => void }) {
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [autoSlug, setAutoSlug] = useState(true);
  const [isVersioned, setIsVersioned] = useState(false);
  const [versionNum, setVersionNum] = useState("1");
  // Seeded FROM the preset shown as selected, not alongside it. These were
  // written out by hand and drifted: the modal opened on "Private team docs",
  // whose preset is internalRole 'none', with internalRole state 'viewer' —
  // so creating without touching a preset produced a project every Joveo
  // user could read, which is not what the selected card says.
  const [presetKey, setPresetKey] = useState(PRESETS[0].key);
  const _p0 = PRESETS[0];
  const [visibility, setVisibility] = useState<"internal" | "external">(_p0.visibility);
  const [internalRole, setInternalRole] =
    useState<"none" | "viewer" | "editor" | "admin">(_p0.internalRole);
  const [shareByDefault, setShareByDefault] = useState(_p0.shareByDefault);
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [partners, setPartners] = useState<Array<{ id: string; slug: string; name: string }>>([]);

  // Create stays disabled until a partner is chosen: without this the preset
  // advertised a one-step path and silently produced a Shared project.
  const needsPartner = !!PRESETS.find(x => x.key === presetKey)?.needsPartner;

  // /api/admin/partners is Depends(require_super_admin), so for anyone else
  // this 403s and the list stays empty — which is how "Partner docs" came to
  // create a Shared project for a non-super user every single time.
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      const r = await authFetch("/api/admin/partners");
      if (r.ok) setPartners(await r.json());
    })();
  }, [isSuperAdmin]);
  const [cloneFrom, setCloneFrom] = useState("");
  const [useProductSections, setUseProductSections] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneCandidates, setCloneCandidates] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const r = await authFetch("/api/admin/projects");
      if (r.ok) setCloneCandidates(await r.json());
    })();
  }, []);

  const toSlug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const finalSlug = isVersioned && versionNum ? `${newSlug}/v${versionNum}` : newSlug;

  const doCreate = async () => {
    if (!newName.trim() || !finalSlug.trim()) return;
    setCreating(true); setCreateErr("");
    try {
      const body: Record<string, unknown> = { name: newName.trim(), slug: finalSlug.trim(), description: newDesc.trim() || null, visibility, is_published: false, internal_role: internalRole, share_by_default: shareByDefault, partner_id: partnerId };
      if (cloneFrom) body.clone_from_project_id = cloneFrom;
      else if (presetKey === "product" && useProductSections) body.template = "product";
      const r = await authFetch("/api/admin/projects", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setCreateErr(d.detail || "Failed to create project"); setCreating(false); return; }
      onCreated(finalSlug.trim());
    } catch { setCreateErr("Network error"); }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col" onClick={e => { e.stopPropagation(); setCloneOpen(false); }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-semibold text-slate-800">New Project</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><X className="size-4" /></button>
        </div>
        <div ref={scrollRef} className="px-5 py-4 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Name</label>
            <input type="text" value={newName} onChange={e => { setNewName(e.target.value); if (autoSlug) setNewSlug(toSlug(e.target.value)); }} placeholder="My Project" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Slug</label>
            <div className="flex items-center gap-2">
              <input type="text" value={newSlug} onChange={e => { setNewSlug(e.target.value.toLowerCase()); setAutoSlug(false); }} placeholder="my-project"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
              {isVersioned && (
                <>
                  <span className="text-slate-300 text-sm select-none">/</span>
                  <input type="text" value={`v${versionNum}`} readOnly
                    className="w-16 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono bg-slate-50 text-slate-500 focus:outline-none cursor-default" />
                </>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">What kind of project is this?</label>
            {/* The four names live here, as presets over the two axes, rather
                than as a stored column. Each one just sets field values, and
                the detail view then shows what is actually stored — so the
                derived label is never a surprise. Public needs a super admin,
                so it is offered but refused server-side for anyone else. */}
            <div className="grid grid-cols-2 gap-1.5">
              {PRESETS.map(ps => {
                // By key. Product and Partner set identical fields and differ
                // only in whether a partner is chosen, so field equality lit
                // both buttons at once.
                const on = presetKey === ps.key;
                return (
                  <button key={ps.key} type="button" onClick={() => {
                    setPresetKey(ps.key);
                    setVisibility(ps.visibility);
                    setInternalRole(ps.internalRole);
                    setShareByDefault(ps.shareByDefault);
                    if (!ps.needsPartner) setPartnerId(null);
                  }}
                    className={`text-left px-3 py-2 rounded-lg border transition-colors ${on ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <div className={`text-xs font-semibold ${on ? "text-indigo-700" : "text-slate-700"}`}>{ps.title}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">{ps.hint}</div>
                  </button>
                );
              })}
            </div>
            {needsPartner && (
              <div className="mt-2">
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Partner</label>
                <select value={partnerId ?? ""} onChange={e => setPartnerId(e.target.value || null)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Select a partner…</option>
                  {partners.map(pt => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
                </select>
                {partners.length === 0 && (
                  <p className="text-[10px] text-amber-600 mt-1">
                    No partners available to you. Only a super admin can create
                    a partner project.
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Description <span className="text-slate-400 font-normal">(optional)</span></label>
            <input type="text" value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Brief description" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 space-y-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={isVersioned} onChange={e => setIsVersioned(e.target.checked)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/20" />
              <div>
                <span className="text-sm font-medium text-slate-700">Versioned project</span>
                <p className="text-[11px] text-slate-400 mt-0.5">Creates a slug like <span className="font-mono">my-project/v1</span></p>
              </div>
            </label>
            {isVersioned && (
              <div className="flex items-center gap-3 pl-7">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Version</span>
                <div className="flex items-center rounded-lg border border-slate-200 bg-white shadow-sm">
                  <button type="button" onClick={() => setVersionNum(String(Math.max(1, Number(versionNum) - 1)))}
                    className="px-2.5 py-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors rounded-l-lg disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                    disabled={Number(versionNum) <= 1}>
                    <Minus className="size-3.5" />
                  </button>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={versionNum}
                    onChange={e => { const v = e.target.value.replace(/\D/g, ""); setVersionNum(v || ""); }}
                    onBlur={() => { if (!versionNum || Number(versionNum) < 1) setVersionNum("1"); }}
                    className="w-10 py-1.5 text-sm font-mono text-center text-slate-700 border-x border-slate-200 bg-transparent focus:outline-none focus:bg-indigo-50/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                  <button type="button" onClick={() => setVersionNum(String(Number(versionNum || "0") + 1))}
                    className="px-2.5 py-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors rounded-r-lg">
                    <Plus className="size-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
          {presetKey === "product" && !cloneFrom && (
            <label className="flex items-start gap-2 text-[13px] text-slate-600">
              <input type="checkbox" checked={useProductSections} onChange={e => setUseProductSections(e.target.checked)} className="mt-0.5" />
              <span>Start with Documentation, Internal sales guidance and Internal operations. The last two are internal only.</span>
            </label>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Clone content from <span className="text-slate-400 font-normal">(optional)</span></label>
            <div className="relative">
              <button type="button" onClick={(e) => { e.stopPropagation(); setCloneOpen(!cloneOpen); if (!cloneOpen) setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50); }}
                className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors">
                <span className={cloneFrom ? "text-slate-800" : "text-slate-400"}>
                  {cloneFrom ? (cloneCandidates.find(p => p.id === cloneFrom)?.name || "Selected") : "Start empty"}
                </span>
                <ChevronDown className={`size-4 text-slate-400 transition-transform ${cloneOpen ? "rotate-180" : ""}`} />
              </button>
              {cloneOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <button type="button" onClick={() => { setCloneFrom(""); setCloneOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${!cloneFrom ? "bg-indigo-50 text-indigo-700" : "text-slate-600"}`}>
                    Start empty
                  </button>
                  {cloneCandidates.filter(p => p.slug !== finalSlug).map(p => (
                    <button key={p.id} type="button" onClick={() => { setCloneFrom(p.id); setCloneOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors border-t border-slate-100 ${cloneFrom === p.id ? "bg-indigo-50 text-indigo-700" : "text-slate-700"}`}>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{p.slug}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Copies docs, API specs, sections, and members from the selected project</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Joveo access</label>
            <div className="flex gap-1.5">
              {(["none", "viewer", "editor", "admin"] as const).map(r => (
                <button key={r} type="button" onClick={() => setInternalRole(r)}
                  className={`flex-1 px-2 py-2 rounded-lg text-[11px] font-medium border transition-colors capitalize ${internalRole === r ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                  {r === "none" ? "No access" : r}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              {internalRole === "none" ? "Only people you explicitly add will have access." :
               internalRole === "viewer" ? "Anyone at Joveo can view this project." :
               internalRole === "editor" ? "Anyone at Joveo can edit content." :
               "Anyone at Joveo has full admin access."}
            </p>
          </div>
          {createErr && <div className="flex items-center gap-2 text-xs text-red-600"><AlertCircle className="size-3.5" /> {createErr}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
          <button onClick={doCreate} disabled={creating || !newName.trim() || !finalSlug.trim()
              || (needsPartner && !partnerId)} className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">{creating ? "Creating…" : "Create Project"}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Slug encoding for versioned projects (slug contains "/") ── */
function encodeProjectSlug(slug: string) { return slug.replace(/\//g, "~"); }
function decodeProjectSlug(encoded: string) { return encoded.replace(/~/g, "/"); }


/* ── Project Detail Route — resolves project from URL ── */
/* One shape for the project row /api/admin/projects returns. It was written
   out four times with three different field sets, and the copy ProjectDetail
   used dropped audience/partner — so audienceOf() saw nothing and every
   project looked private. */
type AdminProject = {
  id: string; name: string; slug: string; description: string | null;
  isPublished: boolean; visibility: string;
  internalRole?: JoveoRole;
  audience?: Audience;
  partner?: { id: string; slug: string; name: string } | null;
  shareByDefault?: boolean;
};

function ProjectDetailInline({ encodedSlug, onProjectUpdated }: { encodedSlug: string; onProjectUpdated?: () => void }) {
  const slug = decodeProjectSlug(encodedSlug);

  const [project, setProject] = useState<AdminProject | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Abort the in-flight fetch when slug changes (or on unmount). Without
    // this, switching projects fast can let an older response finish AFTER
    // the newer one and overwrite the right-side view with stale data.
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch("/api/admin/projects", { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) {
          const all: AdminProject[] = await r.json();
          if (ctrl.signal.aborted) return;
          setProject(all.find(p => p.slug === slug) || null);
        }
        setLoading(false);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [slug]);

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;
  if (!project) return <div className="text-sm text-slate-400 py-8 text-center">Project not found</div>;

  return <ProjectDetail project={project} onProjectUpdated={onProjectUpdated} />;
}


/* ── Default Access moved into each project, as External view ───────── */
function DefaultAccessMoved() {
  const navigate = useNavigate();
  return (
    <div className="max-w-lg py-10 text-center mx-auto">
      <ShieldCheck className="size-6 text-slate-300 mx-auto mb-3" />
      <h2 className="text-[15px] font-semibold text-slate-800">Default access is now External view</h2>
      <p className="text-[12px] text-slate-500 mt-1.5 leading-snug">
        It is a per-project setting now, on the project's own
        <span className="font-medium"> External view</span> tab — and it works
        on every audience that has readers outside Joveo, not just Public.
        Each partner can still override it from their own page.
      </p>
      <button onClick={() => navigate(`${BASE}/projects`, { replace: true })}
        className="mt-4 px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
        Go to Projects
      </button>
    </div>
  );
}

/* ── Project Detail — tabbed view: Branches, Commits, Approvals, Users (admin) ── */

function ProjectDetail({ project, onProjectUpdated }: { project: AdminProject; onProjectUpdated?: () => void }) {
  const { switchProject, isAdmin } = useProject();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Backward compat: legacy `?tab=users`, `?tab=sharing`, and the
  // short-lived `?tab=people` all land on the new "Members" tab; legacy
  // `?tab=links` lands on "Share links"; `?tab=default-access` (the tab's
  // name before it became External view) lands on "External view".
  const rawTab = searchParams.get("tab");
  const tab: ProjectTab = (() => {
    if (rawTab === "users" || rawTab === "sharing" || rawTab === "people") return "members";
    if (rawTab === "links") return "share-links";
    if (rawTab === "default-access") return "external-view";
    return (rawTab as ProjectTab) || "general";
  })();
  const [published, setPublished] = useState(project.isPublished);
  const [toggling, setToggling] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishError, setPublishError] = useState("");
  const encodedSlug = encodeProjectSlug(project.slug);

  useEffect(() => { switchProject(project.slug); }, [project.slug, switchProject]);
  // Rewrite the old key in the URL too, so a bookmark or a link pasted in
  // docs or Slack lands on the tab and copying the URL gives the new name.
  useEffect(() => {
    if (rawTab === "default-access") navigate(`${BASE}/projects/${encodedSlug}?tab=external-view`, { replace: true });
  }, [rawTab, encodedSlug, navigate]);

  const confirmTogglePublish = async () => {
    setToggling(true);
    try {
      const r = await authFetch(`/api/admin/projects/${project.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_published: !published }),
      });
      if (r.ok) {
        setPublished(!published);
        setPublishError("");
      } else {
        // Was swallowed entirely, so the switch animated and snapped back
        // with no explanation — including on the 409 an audience change can
        // return.
        const d = await r.json().catch(() => ({}));
        setPublishError(typeof d.detail === "string" ? d.detail
                        : (d.detail?.error || "Could not change publishing"));
      }
    } catch {
      setPublishError("Could not change publishing");
    }
    setToggling(false);
    setShowPublishDialog(false);
  };

  // Editors only get tabs they can actually act on. Admin-only tabs (project
  // settings, builds, member management) are hidden — the matching backend
  // endpoints already 403 editors via _require_project_admin_by_id, so this
  // is just removing dead UI for them.
  const projectTabs: Array<{ key: ProjectTab; label: string; icon: typeof Settings2; groupStart?: boolean }> = [
    ...(isAdmin ? [{ key: "general" as const, label: "General", icon: Settings2 }] : []),
    { key: "branches", label: "Branches", icon: GitBranch },
    { key: "commits", label: "Commits", icon: History },
    ...(isAdmin ? [{ key: "builds" as const, label: "Builds", icon: Package }] : []),
    { key: "graph", label: "Graph", icon: Network },
    { key: "merge-requests", label: "Requests", icon: GitPullRequest },
    { key: "compare", label: "Compare", icon: GitCompare },
    // Access group: Members (role grants for authenticated accounts) and
    // Share links (tokenized public URLs).
    ...(isAdmin
      ? [
          { key: "external-view" as const, label: "External view", icon: ShieldCheck, groupStart: true },
          { key: "members" as const, label: "Members", icon: Users },
          { key: "share-links" as const, label: "Share links", icon: Link2 },
          { key: "reader-questions" as const, label: "Questions", icon: MessageCircleQuestion, groupStart: true },
        ]
      : []),
  ];

  return (
    <div className="space-y-0">
      {/* Header with project name + publish toggle */}
      <div className="flex items-center justify-between pb-4">
        <div className="flex items-center gap-3">
          <div className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${audienceOf(project) === "private" ? "bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200" : "bg-violet-50 text-violet-600 ring-1 ring-violet-200"}`}><FolderOpen className="size-5" /></div>
          <div>
            <h2 className="text-[17px] font-semibold text-slate-800 leading-tight">{project.name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[11px] font-mono text-slate-400">{project.slug}</span>
              {/* Two chips, because the axes are independent: who outside can
                  read it, and what everyone at Joveo gets. The stored enum used
                  to be rendered here as the label. */}
              <AudienceChip project={project} />
              <JoveoChip role={(project as any).joveoRole} />
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            {/* A non-ok was swallowed, so the switch animated and snapped
                back with no explanation. */}
            {publishError && (
              <span className="text-[11px] text-rose-600 max-w-[22rem] text-right">{publishError}</span>
            )}
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${published ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{published ? "Published" : "Draft"}</span>
            <button
              onClick={() => setShowPublishDialog(true)}
              disabled={toggling}
              className={`relative w-9 h-5 rounded-full transition-colors ${published ? "bg-emerald-500" : "bg-slate-200"} ${toggling ? "opacity-50" : ""}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${published ? "translate-x-4" : ""}`} />
            </button>
          </div>
        )}
      </div>

      {/* Horizontal tab bar — a slim slate divider precedes the "access"
          group (People + Links) so they read as a related pair without
          adding a second header row. */}
      <div className="flex items-center gap-0.5 border-b border-slate-200 -mx-6 px-6 mb-5">
        {projectTabs.map(t => {
          const active = tab === t.key;
          return (
            <span key={t.key} className="flex items-center">
              {t.groupStart && (
                <span className="mx-2 h-4 w-px bg-slate-200" aria-hidden />
              )}
              <button onClick={() => navigate(`${BASE}/projects/${encodedSlug}?tab=${t.key}`, { replace: true })}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium border-b-2 transition-colors -mb-px ${active ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-200"}`}>
                <t.icon className="size-3.5" strokeWidth={1.75} />{t.label}
              </button>
            </span>
          );
        })}
      </div>

      <div className="pt-1">
      {tab === "external-view" && isAdmin && (
        <ProjectDefaultAccessTab
          project={{ id: project.id, slug: project.slug, isPublished: project.isPublished,
                     audience: project.audience, partner: project.partner }}
          isAdmin={isAdmin} />
      )}
      {tab === "external-view" && isAdmin && <WebhooksPanel projectId={project.id} />}
      {tab === "reader-questions" && isAdmin && <ReaderQuestionsTab projectSlug={project.slug} />}
      {tab === "general" && isAdmin && <ProjectGeneralTab project={project} isAdmin={isAdmin} published={published} onPublishChange={setPublished} onProjectUpdated={onProjectUpdated} />}
      {tab === "branches" && <CmsAdminPage defaultTab="branches" />}
      {tab === "commits" && <CmsAdminPage defaultTab="commits" />}
      {tab === "builds" && isAdmin && <CmsAdminPage defaultTab="builds" />}
      {tab === "graph" && <div style={{ height: 500 }}><CommitGraphVisualizer projectSlug={project.slug} /></div>}
      {tab === "merge-requests" && <MergeRequestDashboardPage />}
      {tab === "compare" && <BranchDiffPage />}
      {(tab === "members" || tab === "share-links") && isAdmin && (
        <ProjectSharingTab
          view={tab === "members" ? "members" : "share-links"}
          // audience and partner too: supportsLinkSharing needs them, and the
          // trimmed object made audienceOf fall back for every project, so
          // link sharing was refused everywhere.
          project={{ id: project.id, slug: project.slug, isPublished: published,
                     internalRole: project.internalRole,
                     audience: project.audience, partner: project.partner }}
          onProjectUpdated={(patch) => {
            // Optimistic update for the local project state so other tabs see the new value.
            Object.assign(project, patch);
            onProjectUpdated?.();
          }}
        />
      )}
      {/* Editors who deep-link into an admin-only tab get bounced to Branches. */}
      {!isAdmin && (tab === "general" || tab === "builds" || tab === "members" || tab === "share-links") && (
        <Navigate to={`${BASE}/projects/${encodedSlug}?tab=branches`} replace />
      )}
      </div>

      {/* Publish / Unpublish confirmation dialog */}
      {showPublishDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowPublishDialog(false)}>
          <div className="bg-white rounded-xl shadow-xl w-[380px] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-800">
                {published ? "Unpublish project" : "Publish project"}
              </span>
              <button onClick={() => setShowPublishDialog(false)} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                <X className="size-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-slate-500 leading-relaxed">
                <span className="font-medium text-slate-700">{project.name}</span>
                {published
                  ? " will be unpublished and its content will no longer appear on the docs site."
                  : " will go live on the docs site and be visible to all users with access."}
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
              <button onClick={() => setShowPublishDialog(false)}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                Cancel
              </button>
              <button onClick={confirmTogglePublish} disabled={toggling}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-40 ${published
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"}`}>
                {toggling ? "Updating…" : published ? "Unpublish" : "Publish"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Project General Tab ── */
function ProjectGeneralTab({ project, isAdmin, onProjectUpdated }: {
  project: AdminProject;
  isAdmin: boolean;
  published?: boolean;
  onPublishChange?: (v: boolean) => void;
  onProjectUpdated?: () => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const [name, setName] = useState(project.name);
  const [slug, setSlug] = useState(project.slug);
  const [description, setDescription] = useState(project.description || "");
  const [visibility, setVisibility] = useState(project.visibility);
  const [partnerId, setPartnerId] = useState<string | null>(project.partner?.id ?? null);
  const [shareByDefault, setShareByDefault] = useState(project.shareByDefault ?? false);
  const [partners, setPartners] = useState<Array<{ id: string; slug: string; name: string }>>([]);
  const [dropRules, setDropRules] = useState<Array<{ owner_type: string; owner_id: string | null; slug: string }> | null>(null);
  // What else an audience change cuts off: named outside members and live
  // share links (update_project's 409 counts them). Shown beside the rules.
  const [dropExtra, setDropExtra] = useState<{ members: number; shareLinks: number }>({ members: 0, shareLinks: 0 });
  // Only ever holds "partner": it keeps the selector honest between clicking
  // "One partner" and choosing one, so the control never shows an audience
  // the payload would not produce.
  const [audienceIntent, setAudienceIntent] = useState<"partner" | null>(
    project.partner?.id ? "partner" : null);
  // The partners this viewer administers, from their own profile — no extra
  // endpoint, and the same fact the server gate checks.
  const partnerAdminOf = new Set(
    (user?.partnerMemberships ?? [])
      .filter(m => m.role === "admin")
      .map(m => m.partnerId));

  // The partner list is only needed when the audience selector is in play.
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      const r = await authFetch("/api/admin/partners");
      if (r.ok) setPartners(await r.json());
    })();
  }, [isSuperAdmin]);

  // The audience the selector currently represents. Mirrors
  // project_access.audience_of, so what the UI claims and what the API derives
  // cannot drift.
  const pendingAudience: Audience =
    visibility !== "external" ? "private"
    : partnerId ? "partner"
    : audienceIntent === "partner" ? "partner"
    : shareByDefault ? "public"
    : "shared";
  const partnerIncomplete = pendingAudience === "partner" && !partnerId;

  const setAudience = (a: Audience) => {
    // A 409 panel describes the change it was raised for; a new choice needs
    // its own, or "Remove access and save" would apply drop_rules to a change
    // it never listed.
    setDropRules(null); setDropExtra({ members: 0, shareLinks: 0 });
    setAudienceIntent(a === "partner" ? "partner" : null);
    if (a === "private") { setVisibility("internal"); setPartnerId(null); setShareByDefault(false); }
    if (a === "shared") { setVisibility("external"); setPartnerId(null); setShareByDefault(false); }
    // No partners[0] guess. That mutated visibility while leaving partnerId
    // null, so pendingAudience recomputed to "shared": the button never lit,
    // the picker never appeared, and Save PATCHed Shared with a green tick —
    // which on a Private project WIDENS it.
    if (a === "partner") { setVisibility("external"); setShareByDefault(false); }
    if (a === "public") { setVisibility("external"); setPartnerId(null); setShareByDefault(true); }
  };
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmSlug, setDeleteConfirmSlug] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Detect versioned project from slug pattern (e.g. my-project/v1)
  const slugParts = slug.split("/");
  const isVersioned = slugParts.length > 1 && /^v\d+$/.test(slugParts[slugParts.length - 1]);
  const baseSlug = isVersioned ? slugParts.slice(0, -1).join("/") : slug;
  const versionSlug = isVersioned ? slugParts[slugParts.length - 1] : null;

  const dirty = name !== project.name || slug !== project.slug || description !== (project.description || "") || visibility !== project.visibility || partnerId !== (project.partner?.id ?? null) || shareByDefault !== (project.shareByDefault ?? false);

  const handleSave = async (force = false) => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(), slug: slug.trim(),
        description: description.trim() || null,
      };
      // The audience goes out only when this form changed it. Resending the
      // values loaded with the page reverted any change made since elsewhere
      // — e.g. "Open project" in the header's Share dialog — on the next
      // rename, with a green "Saved", because a project admin may now make
      // Shared -> Private.
      const audienceChanged = visibility !== project.visibility
        || partnerId !== (project.partner?.id ?? null)
        || shareByDefault !== (project.shareByDefault ?? false);
      if (audienceChanged) payload.visibility = visibility;
      // Whoever may CHANGE the audience must also be able to SEND it. This
      // read `if (isSuperAdmin)`, on the premise that sending the fields
      // unchanged would 403 an ordinary project admin editing the name —
      // which is no longer true: update_project gates on the audience TUPLE
      // changing, not on which field names appeared, so resending identical
      // values never trips it. Meanwhile canChangeAudience deliberately
      // unlocks the control for the one non-super caller the server accepts,
      // an admin of every partner concerned. The two disagreed, so every
      // audience change a partner admin was allowed to make omitted
      // partner_id from the body, the server saw no change, and the save
      // came back 200 with a green "Saved" having done nothing.
      if (audienceChanged && canChangeAudience(project, isSuperAdmin, partnerAdminOf, isAdmin)) {
        payload.partner_id = partnerId;
        payload.share_by_default = shareByDefault;
      }
      // A super admin picking an audience here chose it deliberately, so the
      // server's "this would revive old grants" refusal is waived for them;
      // the Share dialog never sends it.
      const params = new URLSearchParams();
      if (force) params.set("drop_rules", "true");
      if (isSuperAdmin) params.set("allow_revive", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const r = await authFetch(
        `/api/admin/projects/${project.id}${qs}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        // 409 = this change would strand doc access rules. The API lists them
        // and waits rather than orphaning them silently, so show them and let
        // the admin decide.
        if (r.status === 409 && d.detail && typeof d.detail === "object" && "rules" in d.detail) {
          setDropRules(d.detail.rules);
          setDropExtra({ members: d.detail.members ?? 0, shareLinks: d.detail.shareLinks ?? 0 });
          setError("");
        } else {
          setError(typeof d.detail === "string" ? d.detail : (d.detail?.error || "Failed to save"));
        }
      } else {
        setDropRules(null);
        setDropExtra({ members: 0, shareLinks: 0 });
        // The page fetched `project` once; make it what the server now holds,
        // or `dirty` and `audienceChanged` keep comparing against the values
        // it loaded with — and a change back to the original audience would
        // never be sent. (ProjectDetail updates it in place the same way.)
        Object.assign(project, {
          name: name.trim(), slug: slug.trim(), description: description.trim() || null,
          visibility, shareByDefault, audience: pendingAudience,
          partner: partnerId
            ? (partners.find(pt => pt.id === partnerId)
               ?? (project.partner?.id === partnerId ? project.partner : { id: partnerId, slug: "", name: "" }))
            : null,
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        onProjectUpdated?.();
        // If slug changed, redirect to the new URL
        if (slug.trim() !== project.slug) {
          navigate(`${BASE}/projects/${encodeProjectSlug(slug.trim())}?tab=general`, { replace: true });
        }
      }
    } catch { setError("Network error"); }
    setSaving(false);
  };

  return (
    <div className="space-y-5 max-w-lg">
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1">Name</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)} disabled={!isAdmin}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-500" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1">Slug</label>
        {isVersioned ? (
          <div className="flex items-center gap-2">
            <input type="text" value={baseSlug} disabled={!isAdmin}
              onChange={e => setSlug(e.target.value.toLowerCase() + "/" + (versionSlug || "v1"))}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-500" />
            <span className="text-slate-300 text-sm select-none">/</span>
            <input type="text" value={versionSlug!} disabled={!isAdmin}
              onChange={e => setSlug(baseSlug + "/" + e.target.value.toLowerCase())}
              className="w-16 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-500" />
          </div>
        ) : (
          <input type="text" value={slug} disabled={!isAdmin}
            onChange={e => setSlug(e.target.value.toLowerCase())}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-500" />
        )}
      </div>
      {/* Who outside Joveo can read this. Was a raw Internal/External pair,
          which cannot express the difference between a partner project and a
          public one — both are 'external' — and left partner_id and
          share_by_default unreachable from the UI entirely. The four options
          map onto exactly the stored fields; audience_of derives the same
          answer server-side. */}
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1.5">Who can read this project</label>
        <div className="grid grid-cols-2 gap-1.5">
          {AUDIENCE_CHOICES.map(c => {
            const on = pendingAudience === c.key;
            // The TRANSITION needs the privilege, not just the target. A
            // non-super admin leaving a Partner or Public project sends a
            // payload that omits partner_id / share_by_default, so the server
            // keeps them, returns 200, and the UI showed "Saved" with an
            // audience that never took.
            // One predicate, mirroring the server gate, instead of a
            // client-side guess. Locking on the target alone let a non-super
            // admin pick an audience the server then ignored (200, green
            // "Saved", nothing changed); locking on "not super" alone shut
            // out the partner admin update_project accepts.
            // Per TARGET, the way the server authorises it: a partner admin
            // may move their own partner's project to Shared or Private, and
            // may hand a Shared or Private one to a partner, but may not
            // touch Public at either end. Asking only about the current
            // audience refused the first two.
            const locked = !isAdmin || !canChangeAudienceTo(
              project, c.key, isSuperAdmin, partnerAdminOf, isAdmin);
            return (
              <button key={c.key} type="button" disabled={locked}
                onClick={() => setAudience(c.key)}
                title={!isAdmin ? "Project admins only"
                       : locked ? "Super admins only" : c.hint}
                className={`text-left px-3 py-2 rounded-lg border transition-colors ${on ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"} disabled:opacity-50 disabled:cursor-default`}>
                <div className={`text-xs font-semibold ${on ? "text-indigo-700" : "text-slate-700"}`}>{c.label}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">{c.hint}</div>
              </button>
            );
          })}
        </div>
        {pendingAudience === "partner" && (
          <div className="mt-2">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">Partner</label>
            {partnerIncomplete && (
              <p className="text-[10px] text-amber-600 mb-1">
                Choose a partner — until you do, this is not a partner project.
              </p>
            )}
            <select value={partnerId ?? ""} disabled={!isSuperAdmin}
              onChange={e => { setPartnerId(e.target.value || null); setDropRules(null); setDropExtra({ members: 0, shareLinks: 0 }); }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-500">
              {partners.length === 0 && <option value="">No partners available</option>}
              {partners.map(pt => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
            </select>
          </div>
        )}
        {dropRules && (() => {
          // Mirrors update_project: a non-super may drop default scope rules
          // and members, never partner or per-person rules.
          const grantRules = dropRules.filter(r => r.owner_type !== "default");
          const needsSuper = !isSuperAdmin && grantRules.length > 0;
          const cutsOff = dropExtra.members > 0 || dropExtra.shareLinks > 0 || grantRules.length > 0;
          return (
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-[11px] text-amber-800 font-medium">
              {cutsOff ? "This change cuts off people outside Joveo:" : "This change deletes the project's External view:"}
            </p>
            <ul className="mt-1 text-[11px] text-amber-800 list-disc pl-4 space-y-0.5">
              {dropExtra.members > 0 && (
                <li>{dropExtra.members} {dropExtra.members === 1 ? "person" : "people"} outside Joveo who {dropExtra.members === 1 ? "was" : "were"} shared with {dropExtra.members === 1 ? "loses" : "lose"} access and {dropExtra.members === 1 ? "leaves" : "leave"} the list.</li>
              )}
              {dropExtra.shareLinks > 0 && (
                <li>{dropExtra.shareLinks} share {dropExtra.shareLinks === 1 ? "link is" : "links are"} revoked.</li>
              )}
              {dropRules.length > 0 && (
                <li>{dropRules.length} doc access {dropRules.length === 1 ? "rule" : "rules"} would grant nobody{needsSuper ? "." : ` and ${dropRules.length === 1 ? "is" : "are"} deleted.`}</li>
              )}
            </ul>
            {dropRules.length > 0 && (
              <ul className="mt-1 text-[10px] text-amber-700 font-mono space-y-0.5 max-h-24 overflow-y-auto">
                {dropRules.map((r, i) => (
                  <li key={i}>{r.owner_type}{r.owner_id ? `:${r.owner_id}` : ""} — {r.slug}</li>
                ))}
              </ul>
            )}
            {needsSuper && (
              <p className="mt-2 text-[11px] text-amber-900">
                {grantRules.length} partner or per-person {grantRules.length === 1 ? "rule" : "rules"} can only be removed by a super admin. Ask one to remove {grantRules.length === 1 ? "it" : "them"} first.
              </p>
            )}
            <div className="flex gap-2 mt-2">
              {!needsSuper && (
                <button type="button" onClick={() => handleSave(true)}
                  className="px-2.5 py-1 rounded-md bg-amber-600 text-white text-[11px] font-medium hover:bg-amber-700">
                  {cutsOff ? "Remove access and save" : "Delete them and save"}
                </button>
              )}
              <button type="button" onClick={() => { setDropRules(null); setDropExtra({ members: 0, shareLinks: 0 }); }}
                className="px-2.5 py-1 rounded-md bg-white border border-amber-300 text-amber-700 text-[11px] font-medium">
                Cancel
              </button>
            </div>
          </div>
          );
        })()}
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1">Description <span className="text-slate-400 font-normal">(optional)</span></label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)} disabled={!isAdmin} placeholder="Brief description"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-500" />
      </div>
      {/* Versioning toggle */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 space-y-3">
        <label className={`flex items-center gap-2.5 ${isAdmin ? "cursor-pointer" : ""}`}>
          <input type="checkbox" checked={isVersioned} disabled={!isAdmin}
            onChange={e => {
              if (e.target.checked) {
                // Add /v1 to slug
                setSlug(slug.replace(/\/$/, "") + "/v1");
              } else {
                // Remove version segment
                const parts = slug.split("/");
                if (parts.length > 1 && /^v\d+$/.test(parts[parts.length - 1])) {
                  setSlug(parts.slice(0, -1).join("/"));
                }
              }
            }}
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/20 disabled:opacity-50" />
          <div>
            <span className="text-sm font-medium text-slate-700">Versioned project</span>
            <p className="text-[11px] text-slate-400 mt-0.5">Creates a slug like <span className="font-mono">my-project/v1</span></p>
          </div>
        </label>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-2">
          <button onClick={() => handleSave()} disabled={saving || !dirty || !name.trim() || partnerIncomplete}
            className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-default transition-colors">
            {saving ? "Saving…" : "Save Changes"}
          </button>
          {saved && <span className="text-xs text-emerald-600">Saved</span>}
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      )}

      {/* Danger Zone — super admins only */}
      {isSuperAdmin && (
        <>
          <div className="border-t border-slate-200 mt-8 pt-6">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-700">Delete this project</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Permanently removes all branches, commits, content, and access rules.</p>
              </div>
              <button onClick={() => { setShowDeleteDialog(true); setDeleteConfirmSlug(""); setDeleteError(""); }}
                className="shrink-0 ml-4 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                Delete project
              </button>
            </div>
          </div>

          {showDeleteDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowDeleteDialog(false)}>
              <div className="bg-white rounded-xl shadow-xl w-[420px] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="size-4 text-red-500" />
                    <span className="text-sm font-semibold text-slate-800">Delete project</span>
                  </div>
                  <button onClick={() => setShowDeleteDialog(false)} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                    <X className="size-4" />
                  </button>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    This will permanently delete <span className="font-semibold text-slate-800">{project.name}</span> and all its data including branches, commits, content nodes, and access rules.
                  </p>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Type <span className="font-mono font-semibold text-slate-700">{project.slug}</span> to confirm
                    </label>
                    <input type="text" value={deleteConfirmSlug} onChange={e => setDeleteConfirmSlug(e.target.value)} placeholder={project.slug}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400" autoFocus />
                  </div>
                  {deleteError && <p className="text-xs text-red-600">{deleteError}</p>}
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
                  <button onClick={() => setShowDeleteDialog(false)}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                    Cancel
                  </button>
                  <button onClick={async () => {
                      setDeleting(true); setDeleteError("");
                      try {
                        const r = await authFetch(`/api/admin/projects/${project.id}`, { method: "DELETE" });
                        if (!r.ok) {
                          const d = await r.json().catch(() => ({}));
                          setDeleteError(d.detail || "Failed to delete");
                        } else {
                          setShowDeleteDialog(false);
                          navigate(BASE, { replace: true });
                        }
                      } catch { setDeleteError("Network error"); }
                      setDeleting(false);
                    }}
                    disabled={deleting || deleteConfirmSlug !== project.slug}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-default transition-colors">
                    {deleting ? "Deleting…" : "Delete permanently"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── App Users — invite across projects or as admin ── */
interface AdminUser {
  id: string; email: string; name: string | null; userType: string;
  status: string; isSuperAdmin: boolean;
  memberships: Array<{ projectId: string; projectName: string; projectSlug: string; role: string }>;
}

type UserFilters = { user_type: string; status: string; role: string; project_id: string };
const NO_USER_FILTERS: UserFilters = { user_type: "", status: "", role: "", project_id: "" };
// Values mirror GET /api/admin/users' query params.
const USER_FILTER_CHOICES: Array<{ key: Exclude<keyof UserFilters, "project_id">; label: string; options: Array<[string, string]> }> = [
  { key: "user_type", label: "Type", options: [["", "All types"], ["internal", "Joveo"], ["external", "External"], ["service", "Service account"]] },
  { key: "role", label: "Role", options: [["", "Any role"], ["super_admin", "Super admin"], ["admin", "Project admin"], ["editor", "Project editor"], ["none", "No projects"]] },
  { key: "status", label: "Status", options: [["", "Any status"], ["active", "Active"], ["pending", "Pending"], ["deactivated", "Deactivated"]] },
];
const filterSelectClass = "text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400";

function AppUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<UserFilters>(NO_USER_FILTERS);

  // Shared dialog state for both Add and Edit
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  // An existing person opens on what they can open; Roles is today's editor.
  const [dlgTab, setDlgTab] = useState<"access" | "roles">("access");
  const rolesTabRef = useRef<HTMLButtonElement | null>(null);
  // Change on the Access tab: switch, and take keyboard focus with it.
  const goToRoles = () => { setDlgTab("roles"); requestAnimationFrame(() => rolesTabRef.current?.focus()); };
  const [dlgEmail, setDlgEmail] = useState("");
  const [dlgRoles, setDlgRoles] = useState<Record<string, "admin" | "editor" | "none">>({});
  const [dlgFilter, setDlgFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (s: string, p: number, ps: number, f: UserFilters) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const params = new URLSearchParams({ limit: String(ps), offset: String(p * ps) });
    if (s.trim()) params.set("search", s.trim());
    for (const [k, v] of Object.entries(f)) if (v) params.set(k, v);
    try {
      const [ur, pr] = await Promise.all([
        authFetch(`/api/admin/users?${params}`, { signal: ctrl.signal }),
        authFetch("/api/admin/projects", { signal: ctrl.signal }),
      ]);
      if (ctrl.signal.aborted) return;
      if (ur.ok) {
        const data = await ur.json();
        setUsers(data.users || data);
        setTotal(data.total ?? (data.users || data).length);
      }
      if (pr.ok) setProjects(await pr.json());
    } catch (e: unknown) { if (e instanceof DOMException && e.name === "AbortError") return; }
    if (!ctrl.signal.aborted) { setLoading(false); setSearching(false); }
  }, []);
  useEffect(() => { load("", 0, 10, NO_USER_FILTERS); }, [load]);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = (v: string) => {
    setSearch(v);
    setSearching(true);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(0); load(v, 0, pageSize, filters); }, 500);
  };
  const handlePageChange = (p: number) => { setPage(p); load(search, p, pageSize, filters); };
  const handlePageSizeChange = (s: number) => { if (searchTimer.current) clearTimeout(searchTimer.current); setPageSize(s); setPage(0); load(search, 0, s, filters); };
  const handleFilterChange = (f: UserFilters) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setFilters(f); setPage(0); load(search, 0, pageSize, f);
  };
  const filtering = Object.values(filters).some(Boolean);

  const visibleUsers = users;

  const openAddDialog = () => {
    setShowAdd(true); setErr(""); setDlgEmail("");
    setDlgRoles(Object.fromEntries(projects.map(p => [p.id, "none" as const])));
    setDlgFilter("");
  };

  const openEditDialog = (u: AdminUser) => {
    setEditUser(u); setErr(""); setDlgFilter(""); setDlgTab("access");
    const roles: Record<string, "admin" | "editor" | "none"> = {};
    for (const p of projects) {
      const mb = u.memberships.find(m => m.projectId === p.id);
      roles[p.id] = mb && (mb.role === "admin" || mb.role === "editor") ? mb.role as "admin" | "editor" : "none";
    }
    setDlgRoles(roles);
  };

  const closeDialog = () => { setShowAdd(false); setEditUser(null); };
  const isDialogOpen = showAdd || !!editUser;

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(dlgFilter.toLowerCase()) || p.slug.toLowerCase().includes(dlgFilter.toLowerCase())
  );

  const doSave = async () => {
    setBusy(true); setErr("");
    try {
      if (showAdd) {
        if (!dlgEmail.trim()) { setErr("Email is required"); setBusy(false); return; }
        let projectRoles: Array<{ project_id: string; role: string }> = [];
        projectRoles = Object.entries(dlgRoles).filter(([, r]) => r !== "none").map(([pid, role]) => ({ project_id: pid, role }));
        if (projectRoles.length === 0) {
          setErr("Select at least one project"); setBusy(false); return;
        }
        const r = await authFetch("/api/admin/invites", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: dlgEmail.trim(), user_type: "internal", project_roles: projectRoles }),
        });
        const d = await r.json();
        if (!r.ok) { setErr(d.detail || "Failed to add user"); setBusy(false); return; }
      } else if (editUser) {
        const memberships: Array<{ project_id: string; role: string }> = [];
        for (const [pid, role] of Object.entries(dlgRoles)) {
          const orig = editUser.memberships.find(m => m.projectId === pid);
          const origRole = orig && (orig.role === "admin" || orig.role === "editor") ? orig.role : "none";
          if (role !== origRole) {
            memberships.push({ project_id: pid, role: role === "none" ? "remove" : role });
          }
        }
        if (memberships.length > 0) {
          await authFetch(`/api/admin/users/${editUser.id}/memberships`, {
            method: "PUT", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ memberships }),
          });
        }
      }
      closeDialog(); load(search, page, pageSize, filters);
    } catch { setErr("Network error"); }
    setBusy(false);
  };

  const RolePills = ({ value, onChange }: { value: "admin" | "editor"; onChange: (v: "admin" | "editor") => void }) => (
    <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
      {(["admin", "editor"] as const).map(r => (
        <button key={r} type="button" onClick={() => onChange(r)}
          className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${value === r ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>
          {r === "admin" ? "Admin" : "Editor"}
        </button>
      ))}
    </div>
  );

  const dialogBody = (
    <div className="space-y-4">
      {(
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">
            Projects <span className="text-indigo-500 font-normal">({Object.values(dlgRoles).filter(r => r !== "none").length} selected)</span>
          </label>
          <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 mb-2">
            <Search className="size-3 text-slate-400 ml-2.5 shrink-0" />
            <input type="text" value={dlgFilter} onChange={e => setDlgFilter(e.target.value)} placeholder="Filter projects…"
              className="flex-1 min-w-0 text-[13px] py-1.5 pr-2 bg-transparent text-slate-700 placeholder:text-slate-400 outline-none" />
          </div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-52 overflow-y-auto">
            {filteredProjects.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">No projects match</div>
            ) : filteredProjects.map(p => {
              const selected = dlgRoles[p.id] !== "none";
              const toggleSelect = () => {
                if (selected) {
                  setDlgRoles(prev => ({ ...prev, [p.id]: "none" }));
                } else {
                  setDlgRoles(prev => ({ ...prev, [p.id]: "admin" }));
                }
              };
              return (
                <div key={p.id} className={`flex items-center gap-2.5 px-3 py-2.5 ${selected ? "bg-indigo-50/20" : ""}`}>
                  <input type="checkbox" checked={selected} onChange={toggleSelect} className="accent-indigo-600 rounded shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="text-sm text-slate-700">{p.name}</span>
                    <AudienceChip project={p} className="ml-1.5" />
                  </div>
                  {selected && (
                    <RolePills value={dlgRoles[p.id] as "admin" | "editor"} onChange={v => setDlgRoles(prev => ({ ...prev, [p.id]: v }))} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {err && <div className="flex items-center gap-2 text-xs text-red-600"><AlertCircle className="size-3.5" /> {err}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-slate-800 flex items-center gap-2">Users <span className="text-[11px] font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md tabular-nums">{total}</span></h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 w-64 focus-within:ring-1 focus-within:ring-indigo-400 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
            {searching ? <Loader2 className="size-3 text-indigo-400 shrink-0 animate-spin" /> : <Search className="size-3 text-slate-400 shrink-0" />}
            <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search…" className="flex-1 text-xs text-slate-700 placeholder:text-slate-400 bg-transparent outline-none" />
          </div>
          <button onClick={openAddDialog} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors">
            <UserPlus className="size-3.5" /> Add User
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {USER_FILTER_CHOICES.map(({ key, label, options }) => (
          <select key={key} value={filters[key]} aria-label={label}
            onChange={e => handleFilterChange({ ...filters, [key]: e.target.value })}
            className={`${filterSelectClass} ${filters[key] ? "border-indigo-300 text-indigo-700 bg-indigo-50/50" : ""}`}>
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
        <select value={filters.project_id} aria-label="Project"
          onChange={e => handleFilterChange({ ...filters, project_id: e.target.value })}
          className={`${filterSelectClass} max-w-56 ${filters.project_id ? "border-indigo-300 text-indigo-700 bg-indigo-50/50" : ""}`}>
          <option value="">Any project</option>
          {[...projects].sort((a, b) => a.name.localeCompare(b.name)).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {filtering && (
          <button type="button" onClick={() => handleFilterChange(NO_USER_FILTERS)}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-1.5 py-1">
            <X className="size-3" /> Clear filters
          </button>
        )}
      </div>

      {loading ? <div className="text-sm text-slate-400 py-8 text-center">Loading…</div> : visibleUsers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-10 text-center">
          <Users className="size-6 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">{search || filtering ? "No users match these filters" : "No users yet"}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {visibleUsers.map(u => {
            const isSelf = u.id === me?.id;
            return (
              <button key={u.id} onClick={() => openEditDialog(u)}
                className={`w-full bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 text-left hover:border-slate-300 transition-colors ${isSelf ? "opacity-60" : ""}`}>
                <div className="size-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-[11px] font-semibold shrink-0">
                  {initials(u.name, u.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">{u.name || u.email}</span>
                    {isSelf && <span className="text-[10px] text-slate-400 shrink-0">(You)</span>}
                    {u.userType !== "internal" && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">{u.userType === "service" ? "Service account" : "External"}</span>}
                    {u.status !== "active" && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0 capitalize">{u.status}</span>}
                    {u.isSuperAdmin
                      ? <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 shrink-0">Super admin</span>
                      : <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">Member of {u.memberships.length}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-400">
                    {u.name && <span className="truncate">{u.email}</span>}
                    {u.name && <span className="text-slate-200">·</span>}
                    <span>Member of {u.memberships.length} project{u.memberships.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <ChevronRight className="size-4 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <PaginationBar total={total} page={page} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />

      {isDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={closeDialog}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
              {showAdd ? (
                <h3 className="text-sm font-semibold text-slate-800">Add User</h3>
              ) : editUser ? (
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">{editUser.name || editUser.email}</h3>
                  {editUser.name && <p className="text-xs text-slate-400 mt-0.5">{editUser.email}</p>}
                  <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 mt-2 w-fit" role="tablist">
                    {/* Roles edit project memberships, which only Joveo people have:
                        a super admin has everything, and the resolver never reads
                        a membership for an outside person or an integration. */}
                    {(editUser.userType !== "internal" || editUser.isSuperAdmin ? ["access"] as const : ["access", "roles"] as const).map(t => (
                      <button key={t} type="button" role="tab" aria-selected={dlgTab === t} onClick={() => setDlgTab(t)}
                        ref={t === "roles" ? rolesTabRef : undefined}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${dlgTab === t ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                        {t === "access" ? "Access" : "Roles"}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <button onClick={closeDialog} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><X className="size-4" /></button>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
              {showAdd && (
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
                  <input type="email" value={dlgEmail} onChange={e => setDlgEmail(e.target.value)} placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" autoFocus />
                </div>
              )}
              {editUser && dlgTab === "access"
                ? <AccountAccessPanel userId={editUser.id} onChangeRoles={goToRoles} />
                : dialogBody}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 shrink-0">
              <button onClick={closeDialog} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                {editUser && dlgTab === "access" ? "Close" : "Cancel"}
              </button>
              {!(editUser && dlgTab === "access") && (
                <button onClick={doSave} disabled={busy || (showAdd && !dlgEmail.trim())}
                  className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {busy ? (showAdd ? "Adding…" : "Saving…") : (showAdd ? "Add User" : "Save Changes")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Partners Empty State — shown when no partner is selected in sidebar ── */
function PartnersEmptyState({ partners, loaded, onPartnersChanged }: {
  partners: Array<{ id: string; slug: string; name: string }>;
  loaded: boolean;
  onPartnersChanged: () => void;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [cName, setCName] = useState(""); const [cSlug, setCSlug] = useState(""); const [slugTouched, setSlugTouched] = useState(false);
  const [creating, setCreating] = useState(false); const [cErr, setCErr] = useState("");

  // Local access config (no API calls until submit)
  const [localAccessMap, setLocalAccessMap] = useState<Record<string, { mode: string; rules: AccessRule[] }>>({});

  // Auto-open create dialog when ?create=1
  useEffect(() => {
    if (searchParams.get("create") === "1") setShowCreate(true);
  }, [searchParams]);

  const autoSlug = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const handleDone = async () => {
    if (!cName.trim()) return;
    setCreating(true); setCErr("");
    const slug = cSlug.trim() || undefined;
    const docAccess = Object.entries(localAccessMap)
      .filter(([, v]) => v.mode !== "default" || v.rules.length > 0)
      .map(([projectId, v]) => ({
        project_id: projectId,
        mode: v.mode,
        rules: v.rules.map(r => ({
          slug: r.slug,
          include_children: r.includeChildren,
        })),
      }));
    const r = await authFetch("/api/admin/partners", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: cName.trim(), slug, doc_access: docAccess }),
    });
    if (r.ok) {
      const created = await r.json();
      onPartnersChanged();
      setShowCreate(false); setCName(""); setCSlug(""); setSlugTouched(false); setCErr(""); setLocalAccessMap({});
      navigate(`${BASE}/partners/${created.slug}?tab=members`, { replace: true });
    } else {
      const d = await r.json().catch(() => ({}));
      setCErr(d.detail || "Failed to create partner");
    }
    setCreating(false);
  };

  const handleCancel = () => {
    setShowCreate(false); setCName(""); setCSlug(""); setSlugTouched(false); setCErr(""); setLocalAccessMap({});
  };

  // Auto-redirect to first partner if available (skip if opening create dialog)
  useEffect(() => {
    if (loaded && partners.length > 0 && searchParams.get("create") !== "1" && !showCreate) {
      navigate(`${BASE}/partners/${partners[0].slug}?tab=members`, { replace: true });
    }
  }, [loaded, partners, navigate, searchParams, showCreate]);

  if (!loaded) return <div className="text-sm text-slate-400 py-8 text-center">Loading partners…</div>;

  // Partners exist but redirect hasn't fired yet — show nothing to avoid flash
  if (partners.length > 0 && !showCreate) return null;

  return (
    <div className="space-y-4">
      {/* Zero partners — onboarding view */}
      {partners.length === 0 && !showCreate && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="size-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
            <Building2 className="size-7 text-indigo-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-1">No partners yet</h2>
          <p className="text-sm text-slate-500 text-center max-w-sm mb-5">
            Partners are groups of external users that share doc access. Create your first partner to start managing external access.
          </p>
          <button onClick={() => { setShowCreate(true); setCErr(""); }}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
            <Plus className="size-4" /> Create Partner
          </button>
        </div>
      )}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={handleCancel}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] min-h-[50vh] flex flex-col mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
              <h3 className="text-sm font-semibold text-slate-800">Create Partner</h3>
              <button onClick={handleCancel} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><X className="size-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Name + Slug inputs */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-600 mb-1">Name</label>
                  <input type="text" value={cName}
                    onChange={e => { setCName(e.target.value); if (!slugTouched) setCSlug(autoSlug(e.target.value)); }}
                    placeholder="Partner name" autoFocus
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-600 mb-1">Slug</label>
                  <input type="text" value={cSlug}
                    onChange={e => { setCSlug(e.target.value); setSlugTouched(true); }}
                    placeholder={autoSlug(cName) || "partner-slug"}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                </div>
              </div>
              {cErr && <div className="flex items-center gap-2 text-xs text-red-600"><AlertCircle className="size-3.5" /> {cErr}</div>}

              {/* Doc Access config — always visible */}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Doc Access</div>
                <LocalPartnerAccessConfig localAccessMap={localAccessMap} onAccessChange={setLocalAccessMap} />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-between items-center shrink-0">
              <p className="text-[11px] text-slate-400">
                {Object.values(localAccessMap).some(v => v.mode !== "default") ? "Access config will be saved with the partner." : ""}
              </p>
              <div className="flex gap-2">
                <button onClick={handleCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
                <button onClick={handleDone} disabled={creating || !cName.trim()}
                  className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {creating ? "Creating…" : "Done"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Partner Detail Content — shown when a partner is selected ── */
interface PartnerUser { id: string; email: string; name: string | null; avatarUrl: string | null; status: string; role: string; }

function PartnerDetailContent({ partnerSlug, partnerTab, sidebarPartners, onPartnersChanged }: {
  partnerSlug: string;
  partnerTab: "members" | "access" | "products" | "apps";
  sidebarPartners?: Array<{ id: string; slug: string; name: string }>;
  onPartnersChanged?: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<{ id: string; name: string; slug: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<PartnerUser[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberSearch, setMemberSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addMode, setAddMode] = useState<"invite" | "existing">("invite");
  const [addEmail, setAddEmail] = useState("");
  const [addExistingId, setAddExistingId] = useState("");
  const [externalUsers, setExternalUsers] = useState<{ id: string; email: string; name: string | null }[]>([]);
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState<{ title: string; message: string; confirmLabel: string; onConfirm: () => void } | null>(null);

  // Resolve partner from slug — aborts on slug change so a slow response
  // for the previous partner can't overwrite the new partner's data.
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch("/api/admin/partners", { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) {
          const all: { id: string; name: string; slug: string }[] = await r.json();
          if (ctrl.signal.aborted) return;
          setPartner(all.find(p => p.slug === partnerSlug) || null);
        }
        setLoading(false);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [partnerSlug]);

  // Load members when partner is resolved. Effect-style so we can attach
  // an AbortController; the previous in-flight `/users` fetch is cancelled
  // whenever partner.id changes (e.g. user navigates A→B→A quickly).
  useEffect(() => {
    if (!partner) return;
    const ctrl = new AbortController();
    (async () => {
      setLoadingMembers(true);
      try {
        // ponytail: one unpaged fetch (the API defaults to 50); page it if a partner nears 1000 members
        const r = await authFetch(`/api/admin/partners/${partner.id}/users?limit=1000`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) {
          const data = await r.json();
          if (ctrl.signal.aborted) return;
          setMembers(Array.isArray(data) ? data : (data.users || []));
        }
        setLoadingMembers(false);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setLoadingMembers(false);
      }
    })();
    return () => ctrl.abort();
  }, [partner?.id]);

  // Imperative reload after add/remove operations.
  const loadMembers = useCallback(async () => {
    if (!partner) return;
    setLoadingMembers(true);
    const r = await authFetch(`/api/admin/partners/${partner.id}/users?limit=1000`);
    if (r.ok) {
      const data = await r.json();
      setMembers(Array.isArray(data) ? data : (data.users || []));
    }
    setLoadingMembers(false);
  }, [partner?.id]);

  const openAddDialog = async () => {
    setShowAdd(true); setAddErr(""); setAddEmail(""); setAddMode("invite"); setAddExistingId("");
    if (!partner) return;
    // ponytail: whole list (the API defaults to 50); switch to a ?search= typeahead past 1000 external users
    const r = await authFetch("/api/admin/external-users?limit=1000");
    if (r.ok) {
      const data = await r.json();
      const all = Array.isArray(data) ? data : (data.users || []);
      const memberIds = new Set(members.map(u => u.id));
      setExternalUsers(all.filter((u: any) => !memberIds.has(u.id)));
    }
  };

  const handleAddUser = async () => {
    if (!partner) return;
    setAdding(true); setAddErr("");
    if (addMode === "existing") {
      if (!addExistingId) { setAddErr("Select a user"); setAdding(false); return; }
      const r = await authFetch(`/api/admin/external-users/${addExistingId}/partners`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ partner_id: partner.id }),
      });
      if (r.ok) { setShowAdd(false); loadMembers(); }
      else { const d = await r.json().catch(() => ({})); setAddErr(d.detail || "Failed to add user"); }
    } else {
      if (!addEmail.trim()) { setAddErr("Email is required"); setAdding(false); return; }
      const r = await authFetch(`/api/admin/partners/${partner.id}/invites`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: addEmail.trim() }),
      });
      if (r.ok) { setAddEmail(""); setShowAdd(false); loadMembers(); }
      else { const d = await r.json().catch(() => ({})); setAddErr(d.detail || "Failed to add user"); }
    }
    setAdding(false);
  };

  const removeUser = async (userId: string, label: string) => {
    if (!partner) return;
    const failed = async (r: Response) => {
      const d = await r.json().catch(() => ({}));
      setConfirmDlg({
        title: "Couldn't remove",
        message: typeof d.detail === "string" ? d.detail : "The request failed.",
        confirmLabel: "OK", onConfirm: () => setConfirmDlg(null),
      });
    };
    // Check how many partners this user belongs to, and what was shared with them
    const ur = await authFetch(`/api/admin/external-users/${userId}`);
    if (!ur.ok) { await failed(ur); return; }
    const userData = await ur.json();
    const membershipCount = userData.partnerMemberships?.length ?? 0;
    const shared: string[] = userData.sharedProjects ?? [];

    if (membershipCount <= 1 && shared.length === 0) {
      // Nothing outlives the partner: delete the account (or withdraw the
      // invite) entirely, as before — a kept partnerless account would still
      // read every project open to anyone with Cortex.
      setConfirmDlg({
        title: userData.status === "invited" ? "Withdraw invite" : "Delete user",
        message: userData.status === "invited"
          ? `Withdraw the invite for "${label}"? It belongs to no other partner.`
          : `"${label}" has no other partner assignments. This will delete the user entirely.`,
        confirmLabel: userData.status === "invited" ? "Withdraw" : "Delete User",
        onConfirm: async () => {
          setConfirmDlg(null);
          const r = await authFetch(`/api/admin/external-users/${userId}`, { method: "DELETE" });
          if (r.ok) loadMembers(); else await failed(r);
        },
      });
      return;
    }
    // Projects were shared with them by name (self-serve sharing), so deleting
    // the account would silently revoke colleagues' shares: keep it, and say
    // what it can still read.
    const others = membershipCount - 1;
    setConfirmDlg({
      title: "Remove from partner",
      message: membershipCount <= 1
        ? `Remove "${label}" from ${partner.name}? They keep their account because ${shared.length === 1 ? `"${shared[0]}" was` : `${shared.length} projects were`} shared with them by name, and a kept account can also read every project open to anyone with Cortex. Delete the account from External Users to cut all of that off.`
        : `Remove "${label}" from ${partner.name}? They'll still belong to ${others} other partner${others !== 1 ? "s" : ""}.`,
      confirmLabel: "Remove",
      onConfirm: async () => {
        setConfirmDlg(null);
        const r = await authFetch(`/api/admin/external-users/${userId}/partners/${partner.id}`, { method: "DELETE" });
        if (r.ok) loadMembers(); else await failed(r);
      },
    });
  };

  const statusBadge = (status: string) => {
    if (status === "active") return "bg-emerald-50 text-emerald-600";
    if (status === "invited") return "bg-sky-50 text-sky-600";
    return "bg-slate-100 text-slate-500";
  };

  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const handleSaveName = async () => {
    if (!partner || !editName.trim() || editName.trim() === partner.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    const r = await authFetch(`/api/admin/partners/${partner.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: editName.trim() }),
    });
    if (r.ok) {
      setPartner({ ...partner, name: editName.trim() });
      onPartnersChanged?.();
    }
    setSavingName(false);
    setEditingName(false);
  };

  // ── Enable product entitlements ──
  const [products, setProducts] = useState<{ id: string; slug: string; name: string; entitled: boolean }[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [togglingProduct, setTogglingProduct] = useState<string | null>(null);

  useEffect(() => {
    if (!partner) return;
    const ctrl = new AbortController();
    (async () => {
      setLoadingProducts(true);
      try {
        const r = await authFetch(`/api/admin/partners/${partner.id}/entitlements`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) { const d = await r.json(); if (!ctrl.signal.aborted) setProducts(d.products || []); }
        setLoadingProducts(false);
      } catch (e) { if ((e as Error).name !== "AbortError") setLoadingProducts(false); }
    })();
    return () => ctrl.abort();
  }, [partner?.id]);

  const toggleEntitlement = async (productId: string, entitled: boolean) => {
    if (!partner) return;
    setTogglingProduct(productId);
    const r = await authFetch(`/api/admin/partners/${partner.id}/entitlements/${productId}`, {
      method: entitled ? "DELETE" : "PUT",
    });
    if (r.ok) setProducts(ps => ps.map(p => (p.id === productId ? { ...p, entitled: !entitled } : p)));
    setTogglingProduct(null);
  };

  // ── Per-partner app access (Docs is a real toggle; Enable is derived from products) ──
  const [apps, setApps] = useState<{ slug: string; name: string; enabled: boolean; managed: boolean; note?: string }[]>([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [togglingApp, setTogglingApp] = useState<string | null>(null);

  useEffect(() => {
    if (!partner) return;
    const ctrl = new AbortController();
    (async () => {
      setLoadingApps(true);
      try {
        const r = await authFetch(`/api/admin/partners/${partner.id}/apps`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) { const d = await r.json(); if (!ctrl.signal.aborted) setApps(d.apps || []); }
        setLoadingApps(false);
      } catch (e) { if ((e as Error).name !== "AbortError") setLoadingApps(false); }
    })();
    return () => ctrl.abort();
  }, [partner?.id]);

  const toggleApp = async (slug: string, enabled: boolean) => {
    if (!partner) return;
    setTogglingApp(slug);
    const r = await authFetch(`/api/admin/partners/${partner.id}/apps/${slug}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: !enabled }),
    });
    if (r.ok) setApps(as => as.map(a => (a.slug === slug ? { ...a, enabled: !enabled } : a)));
    setTogglingApp(null);
  };

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;
  if (!partner) return <div className="text-sm text-slate-400 py-8 text-center">Partner not found</div>;

  const deletePartner = async () => {
    setConfirmDlg({
      title: "Delete partner",
      message: `Delete "${partner.name}"? This will remove all memberships and access config.`,
      confirmLabel: "Delete",
      onConfirm: async () => {
        setConfirmDlg(null);
        setDeleting(true);
        const r = await authFetch(`/api/admin/partners/${partner.id}`, { method: "DELETE" });
        if (r.ok) {
          // Navigate to previous partner (or next, or list if none left)
          const idx = sidebarPartners?.findIndex(p => p.slug === partnerSlug) ?? -1;
          const remaining = sidebarPartners?.filter(p => p.slug !== partnerSlug) ?? [];
          onPartnersChanged?.();
          if (remaining.length === 0) {
            navigate(`${BASE}/partners`, { replace: true });
          } else {
            const target = idx > 0 ? remaining[idx - 1] : remaining[0];
            navigate(`${BASE}/partners/${target.slug}?tab=${partnerTab}`, { replace: true });
          }
        }
        else {
          const d = await r.json().catch(() => ({}));
          setConfirmDlg({ title: "Error", message: d.detail || "Failed to delete", confirmLabel: "OK", onConfirm: () => setConfirmDlg(null) });
        }
        setDeleting(false);
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0"><Building2 className="size-4" /></div>
          <div>
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text" value={editName} onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleSaveName(); if (e.key === "Escape") setEditingName(false); }}
                  autoFocus
                  className="text-[16px] font-semibold text-slate-800 leading-tight px-2 py-0.5 border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
                <button onClick={handleSaveName} disabled={savingName} className="px-2 py-0.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">Save</button>
                <button onClick={() => setEditingName(false)} className="px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-100 rounded-md">Cancel</button>
              </div>
            ) : (
              <h2 className="text-[16px] font-semibold text-slate-800 leading-tight cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => { setEditName(partner.name); setEditingName(true); }}>{partner.name}</h2>
            )}
            <span className="text-[11px] font-mono text-slate-400">{partner.slug}</span>
          </div>
        </div>
        <button onClick={deletePartner} disabled={deleting}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50">
          <Trash2 className="size-3.5" /> {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button onClick={() => navigate(`${BASE}/partners/${partnerSlug}?tab=members`, { replace: true })} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${partnerTab === "members" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Members {members.length > 0 && <span className="text-slate-400 ml-1">{members.length}</span>}
        </button>
        <button onClick={() => navigate(`${BASE}/partners/${partnerSlug}?tab=access`, { replace: true })} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${partnerTab === "access" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Access Control
        </button>
        <button onClick={() => navigate(`${BASE}/partners/${partnerSlug}?tab=products`, { replace: true })} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${partnerTab === "products" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Products
        </button>
        <button onClick={() => navigate(`${BASE}/partners/${partnerSlug}?tab=apps`, { replace: true })} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${partnerTab === "apps" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Apps
        </button>
      </div>

      {/* Members tab */}
      {partnerTab === "members" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Members ({members.length})</span>
            <button onClick={openAddDialog} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"><UserPlus className="size-3.5" /> Add User</button>
          </div>

          {showAdd && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setShowAdd(false)}>
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
                  <h3 className="text-sm font-semibold text-slate-800">Add User to {partner.name}</h3>
                  <button onClick={() => setShowAdd(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><X className="size-4" /></button>
                </div>
                <div className="px-5 py-4 space-y-4">
                  <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
                    <button onClick={() => setAddMode("invite")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${addMode === "invite" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Invite New</button>
                    <button onClick={() => setAddMode("existing")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${addMode === "existing" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Add Existing</button>
                  </div>
                  {addMode === "invite" ? (
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
                      <input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)} placeholder="user@example.com" autoFocus
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Select User</label>
                      {externalUsers.length === 0 ? (
                        <p className="text-xs text-slate-400 py-2">No available external users to add.</p>
                      ) : (
                        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-52 overflow-y-auto">
                          {externalUsers.map(u => {
                            const selected = addExistingId === u.id;
                            return (
                              <button key={u.id} onClick={() => setAddExistingId(u.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${selected ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
                                <div className="size-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-medium text-slate-500 shrink-0">
                                  {(u.name || u.email)[0].toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-slate-700 truncate">{u.name || u.email}</div>
                                  {u.name && <div className="text-[11px] text-slate-400 truncate">{u.email}</div>}
                                </div>
                                {selected && <Check className="size-4 text-indigo-600 shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {addErr && <div className="flex items-center gap-2 text-xs text-red-600"><AlertCircle className="size-3.5" /> {addErr}</div>}
                </div>
                <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                  <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
                  <button onClick={handleAddUser} disabled={adding || (addMode === "invite" ? !addEmail.trim() : !addExistingId)}
                    className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                    {adding ? "Adding…" : addMode === "invite" ? "Send Invite" : "Add User"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {loadingMembers ? <div className="text-sm text-slate-400 py-8 text-center">Loading…</div> : (() => {
            const filtered = memberSearch.trim()
              ? members.filter(u => (u.name || "").toLowerCase().includes(memberSearch.toLowerCase()) || u.email.toLowerCase().includes(memberSearch.toLowerCase()))
              : members;
            return filtered.length === 0 && members.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-8 text-center"><Users className="size-6 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-400">No members yet.</p></div>
            ) : (
              <>
                {members.length > 5 && (
                  <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 w-64 focus-within:ring-1 focus-within:ring-indigo-400 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
                    <Search className="size-3 text-slate-400 shrink-0" />
                    <input value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder="Search…" className="flex-1 text-xs text-slate-700 placeholder:text-slate-400 bg-transparent outline-none" />
                  </div>
                )}
                {filtered.length === 0 ? (
                  <div className="text-sm text-slate-400 py-4 text-center">No members match your search</div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
                    {filtered.map(u => (
                <div key={u.id} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    {u.avatarUrl ? <img src={u.avatarUrl} className="size-7 rounded-full" alt="" /> : (
                      <div className="size-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-medium text-slate-500">{(u.name || u.email)[0].toUpperCase()}</div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-slate-700">{u.name || u.email}</div>
                      {u.name && <div className="text-xs text-slate-400">{u.email}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusBadge(u.status)}`}>{u.status}</span>
                    {u.id !== user?.id && (
                      <button onClick={() => removeUser(u.id, u.name || u.email)} className="p-1 text-slate-300 hover:text-red-500 transition-colors" title="Remove"><Trash2 className="size-3.5" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
          );
          })()}
        </div>
      )}

      {/* Access Control tab */}
      {partnerTab === "access" && <PartnerAccessConfig partnerId={partner.id} />}

      {/* Products tab — Enable product entitlements */}
      {partnerTab === "products" && (
        <div className="space-y-3">
          <div>
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Enable products</span>
            <p className="text-xs text-slate-400 mt-1">Which Enable products this partner can use. A partner appears in the Enable app once it has at least one.</p>
          </div>
          {loadingProducts ? (
            <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
          ) : products.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">No Enable products configured.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {products.map(p => (
                <div key={p.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700">{p.name}</div>
                    <div className="text-[11px] font-mono text-slate-400">{p.slug}</div>
                  </div>
                  <button
                    role="switch" aria-checked={p.entitled} aria-label={`${p.entitled ? "Disable" : "Enable"} ${p.name}`}
                    onClick={() => toggleEntitlement(p.id, p.entitled)} disabled={togglingProduct === p.id}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${p.entitled ? "bg-indigo-600" : "bg-slate-200"}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${p.entitled ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Apps tab — which Cortex apps this partner's members can use */}
      {partnerTab === "apps" && (
        <div className="space-y-3">
          <div>
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Apps</span>
            <p className="text-xs text-slate-400 mt-1">Which Cortex apps this partner's members can use. Docs is on by default; Enable turns on automatically when the partner has a product.</p>
          </div>
          {loadingApps ? (
            <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
          ) : (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {apps.map(a => (
                <div key={a.slug} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700">{a.name}</div>
                    <div className="text-[11px] text-slate-400">{a.managed ? a.slug : (a.note || a.slug)}</div>
                  </div>
                  {a.managed ? (
                    <button role="switch" aria-checked={a.enabled} aria-label={`${a.enabled ? "Disable" : "Enable"} ${a.name}`}
                      onClick={() => toggleApp(a.slug, a.enabled)} disabled={togglingApp === a.slug}
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${a.enabled ? "bg-indigo-600" : "bg-slate-200"}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${a.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  ) : (
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${a.enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`} title="Managed via the Products tab">
                      {a.enabled ? "On" : "Off"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {confirmDlg && (
        <ConfirmDialog title={confirmDlg.title} message={confirmDlg.message}
          confirmLabel={confirmDlg.confirmLabel} destructive
          onConfirm={confirmDlg.onConfirm} onCancel={() => setConfirmDlg(null)} />
      )}
    </div>
  );
}
interface DocSection { id: string; slug: string; title: string; sectionType: string; }
interface TreeNode { id: string; title: string; slug: string; slugPath: string; children?: TreeNode[]; }

/* ── Tree helpers (rule-based, keyed by slugPath) ── */
type CheckState = "checked" | "partial" | "unchecked";
function collectDescendantSlugPaths(node: TreeNode): string[] {
  const paths: string[] = [];
  for (const c of node.children ?? []) { paths.push(c.slugPath); paths.push(...collectDescendantSlugPaths(c)); }
  return paths;
}
function collectAllContentSlugPaths(nodes: TreeNode[]): string[] {
  const paths: string[] = [];
  for (const n of nodes) { paths.push(n.slugPath); paths.push(...collectDescendantSlugPaths(n)); }
  return paths;
}

/** Find a node by slugPath in a tree */
function findNodeBySlugPath(nodes: TreeNode[], slugPath: string): TreeNode | null {
  for (const n of nodes) {
    if (n.slugPath === slugPath) return n;
    if (n.children?.length) {
      const found = findNodeBySlugPath(n.children, slugPath);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Uncheck a node: if it has its own rule, remove it + descendants.
 * If it's covered by an ancestor's include_children, "split" the ancestor —
 * remove ancestor rule, add include_children for siblings at each level down.
 */
function computeUncheck(
  slugPath: string,
  rules: AccessRule[],
  parentMap: Map<string, string | null>,
  allTrees: TreeNode[],
): AccessRule[] {
  const ruleMap = buildRuleMap(rules);
  const nodeInTree = findNodeBySlugPath(allTrees, slugPath);
  const descPaths = nodeInTree ? new Set(collectDescendantSlugPaths(nodeInTree)) : new Set<string>();
  let next = rules.filter(r => r.slug !== slugPath && !descPaths.has(r.slug));

  let ancestorPath: string | null = null;
  let pid = parentMap.get(slugPath) ?? null;
  while (pid) {
    if (ruleMap.get(pid)?.includeChildren) { ancestorPath = pid; break; }
    pid = parentMap.get(pid) ?? null;
  }

  if (!ancestorPath) return next;

  next = next.filter(r => r.slug !== ancestorPath);

  let currentPath = ancestorPath;
  while (currentPath !== slugPath) {
    const currentNode = findNodeBySlugPath(allTrees, currentPath);
    if (!currentNode?.children?.length) break;

    let pathChildPath: string | null = null;
    for (const child of currentNode.children) {
      if (child.slugPath === slugPath || findNodeBySlugPath(child.children ?? [], slugPath)) {
        pathChildPath = child.slugPath;
        break;
      }
    }
    if (!pathChildPath) break;

    for (const child of currentNode.children) {
      if (child.slugPath !== pathChildPath && !next.some(r => r.slug === child.slugPath)) {
        next.push({ slug: child.slugPath, includeChildren: true });
      }
    }

    if (!next.some(r => r.slug === currentPath)) {
      next.push({ slug: currentPath, includeChildren: false });
    }

    currentPath = pathChildPath;
  }

  return next;
}

/** Build a lookup: slug → rule */
function buildRuleMap(rules: AccessRule[]): Map<string, AccessRule> {
  const m = new Map<string, AccessRule>();
  for (const r of rules) m.set(r.slug, r);
  return m;
}

/** Build parent map from tree (slugPath → parent slugPath) */
function buildParentMap(nodes: TreeNode[], parentSlugPath: string | null, out: Map<string, string | null>) {
  for (const n of nodes) {
    out.set(n.slugPath, parentSlugPath);
    if (n.children?.length) buildParentMap(n.children, n.slugPath, out);
  }
}

/** Check if a node is covered by an ancestor's include_children=true */
function isAncestorCovered(slugPath: string, parentMap: Map<string, string | null>, ruleMap: Map<string, AccessRule>): boolean {
  let pid = parentMap.get(slugPath) ?? null;
  while (pid) {
    const rule = ruleMap.get(pid);
    if (rule?.includeChildren) return true;
    pid = parentMap.get(pid) ?? null;
  }
  return false;
}

/** Determine check state for a node given a combined rule map */
function getNodeCheckState(node: TreeNode, ruleMap: Map<string, AccessRule>, parentMap: Map<string, string | null>): CheckState {
  const rule = ruleMap.get(node.slugPath);
  if (rule?.includeChildren) return "checked";
  if (isAncestorCovered(node.slugPath, parentMap, ruleMap)) return "checked";
  if (rule) {
    if (!node.children?.length) return "checked";
    const allDesc = collectDescendantSlugPaths(node);
    const allChecked = allDesc.every(sp => ruleMap.has(sp) || isAncestorCovered(sp, parentMap, ruleMap));
    return allChecked ? "checked" : "partial";
  }
  if (node.children?.length) {
    const anyChild = node.children.some(c => getNodeCheckState(c, ruleMap, parentMap) !== "unchecked");
    return anyChild ? "partial" : "unchecked";
  }
  return "unchecked";
}

/* ── Partner Access Config — list of projects, click → modal ── */
function PartnerAccessConfig({ partnerId }: { partnerId: string }) {
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [openProject, setOpenProject] = useState<{ id: string; slug: string; name: string; audience?: Audience } | null>(null);
  // The server's answer per project (partner_project_reach): can this
  // partner's people read it, and why. null until it arrives — "can't read"
  // for everything in the meantime would be a lie.
  const [reach, setReach] = useState<Record<string, PartnerReach> | null>(null);

  useEffect(() => {
    (async () => {
      const res = await authFetch("/api/projects");
      if (res.ok) setAllProjects(await res.json());
      setLoadingProjects(false);
    })();
  }, []);

  // Filtered at render, not in the effect. Today the deps-[] version is
  // correct only because switching partners in the sidebar remounts this
  // (the parent shows a loading state in between) — I checked, the stale
  // list does not actually appear. That is the parent's render timing
  // holding up a grantee check, which is not where this belongs.
  //
  // Grantee-aware. supportsNamedExternalAccess alone offered a partner-locked
  // project to the wrong partner, and the API then 422s on save — the admin
  // picks a project, ticks subtrees and only then finds out.
  const projects = allProjects.filter((p: any) => supportsPartnerAccess(p, partnerId));

  const reloadReach = useCallback(async () => {
    const r = await authFetch(`/api/admin/partners/${partnerId}/doc-access-summary`);
    setReach(r.ok ? await r.json() : {});
  }, [partnerId]);
  useEffect(() => { if (partnerId) reloadReach(); }, [partnerId, reloadReach]);

  if (loadingProjects || !reach) return <div className="py-4 text-center text-xs text-slate-400">Loading…</div>;
  if (!projects.length) return <div className="py-4 text-center text-xs text-slate-400">No external projects found.</div>;

  const canRead = projects.filter(p => reach[p.id]?.canRead);
  const cannot = projects.filter(p => !reach[p.id]?.canRead);

  const row = (p: any) => {
    const r = reach[p.id];
    return (
      <button key={p.id} onClick={() => setOpenProject(p)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
          openProject?.id === p.id ? "border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200" : "border-slate-200 hover:border-slate-300 bg-white"
        }`}>
        {r?.canRead
          ? <Check className="size-4 text-emerald-600 shrink-0" aria-label="Can read" />
          : <X className="size-4 text-slate-300 shrink-0" aria-label="Can't read" />}
        <div className="flex-1 min-w-0">
          <div className={`text-[13px] font-medium ${r?.canRead ? "text-slate-800" : "text-slate-500"}`}>{p.name}</div>
          <div className="text-[11px] text-slate-500">{describePartnerReach(r)}</div>
        </div>
        <span className="text-[11px] text-indigo-600 shrink-0">{r?.canRead ? "Change" : "Give access"}</span>
        <ChevronRight className="size-3.5 text-slate-400 shrink-0" />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <section aria-label="Can read">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Can read ({canRead.length})</h3>
        {canRead.length
          ? <div className="space-y-1">{canRead.map(row)}</div>
          : <p className="text-xs text-slate-400 px-1">Nothing yet. Pick a project below to give access.</p>}
      </section>
      {cannot.length > 0 && (
        <section aria-label="Can't read">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Can't read ({cannot.length})</h3>
          <div className="space-y-1">{cannot.map(row)}</div>
        </section>
      )}
      {openProject && (
        <PartnerProjectModal key={openProject.id} partnerId={partnerId} project={openProject}
          reach={reach[openProject.id]} onChanged={reloadReach}
          onClose={() => setOpenProject(null)} />
      )}
    </div>
  );
}

/* ── Partner project modal — mode selector + tree with readonly defaults ── */
function PartnerProjectModal({ partnerId, project, reach, onChanged, onClose }: {
  partnerId: string; project: { id: string; slug: string; name: string; audience?: Audience };
  /** The list's row for this project — kept fresh through onChanged, so the
   *  modal and the list never describe the same partner two ways. */
  reach?: PartnerReach; onChanged: () => void; onClose: () => void;
}) {
  const [sections, setSections] = useState<DocSection[]>([]);
  const [docTrees, setDocTrees] = useState<Record<string, TreeNode[]>>({});
  const [localRules, setLocalRules] = useState<AccessRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [resetDlg, setResetDlg] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const { config, loading, load, save, clear } = usePartnerDocAccess(partnerId, project.id);
  // Which of the three resolutions this partner gets. The modal always SAVED
  // "override", so the project default was never inherited by anybody — all
  // 20 live rows were override, and _resolve_partner_rules' 'default' and
  // 'default_plus' branches were dead. The read path has always supported
  // all three.
  const [mode, setMode] = useState<"default" | "default_plus" | "override">("override");

  useEffect(() => { load(); }, [load]);
  // Nothing that lets them in yet — no row, or an Override with nothing
  // ticked: offer the project's External view first. It is the common ask,
  // and since 2026-10-13 a grant in its own right.
  const empty = !config.mode || (config.mode === "override" && !config.rules?.length);
  const startMode = empty ? "default" : config.mode!;
  useEffect(() => { setMode(startMode); }, [startMode]);
  const canRead = !!reach?.canRead;
  // Own settings to remove — even ones that grant nothing, so a dead row can go.
  const hasOwn = !!config.mode;

  // Pre-fill, per mode. This read `config.mode === "override" && rules.length`
  // and seeded from the DEFAULT rules otherwise — correct while override was
  // the only mode the modal could save, and destructive the moment
  // default_plus became selectable: a default_plus partner's own extras live
  // in config.rules, so the else branch threw them away and the next save
  // wrote the project default over them as that partner's rules.
  //
  // It also used to setDirty(true) whenever mode was 'default' with defaults
  // present, meaning "seeded, nothing saved yet". 'default' is now also the
  // state you reach by deliberately saving "Inherit default", so that flipped
  // a just-saved modal straight back to unsaved.
  useEffect(() => {
    // ?? [] on both: a response that is not the expected
    // {mode, rules, defaultRules} leaves these undefined, and buildRuleMap
    // spreads them — "rules is not iterable" takes the whole modal down
    // through the error boundary.
    if (config.mode === "default" || empty) {
      // Nothing of their own: show what they inherit, read-only.
      setLocalRules(config.defaultRules ?? []);
    } else {
      setLocalRules(config.rules ?? []);
    }
    setDirty(false);
  }, [config]);

  useEffect(() => {
    (async () => {
      setContentLoading(true);
      const secRes = await authFetch(`/api/docs/${project.slug}/sections`);
      if (!secRes.ok) { setContentLoading(false); return; }
      const secs: DocSection[] = await secRes.json();
      setSections(secs);
      const trees: Record<string, TreeNode[]> = {};
      const treeRes = await authFetch(`/api/docs/${project.slug}/tree`);
      const fullTree = treeRes.ok ? (await treeRes.json()).tree || [] : [];
      for (const s of secs) {
        const sectionNode = fullTree.find((n: any) => n.slug === s.slug);
        trees[s.id] = sectionNode?.children || fullTree;
      }
      setDocTrees(trees);
      setContentLoading(false);
    })();
  }, [project.slug]);

  // Build parent map keyed by slugPath (sections are roots; their slugPath is sec.slug).
  const parentMap = new Map<string, string | null>();
  for (const sec of sections) {
    parentMap.set(sec.slug, null);
    buildParentMap(docTrees[sec.id] ?? [], sec.slug, parentMap);
  }

  const ruleMap = buildRuleMap(localRules);

  // Flatten all trees for node lookup. Each section gets a synthetic wrapper whose slugPath = sec.slug.
  const allTrees: TreeNode[] = [];
  for (const sec of sections) {
    allTrees.push({ id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] });
  }

  const toggleSection = (sec: DocSection) => {
    setDirty(true);
    const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
    const state = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(sec.slug, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectDescendantSlugPaths(sectionAsNode));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== sec.slug);
        next.push({ slug: sec.slug, includeChildren: true });
        return next;
      });
    }
  };

  const toggleNode = (slugPath: string, node: TreeNode) => {
    setDirty(true);
    const state = getNodeCheckState(node, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(slugPath, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectDescendantSlugPaths(node));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== slugPath);
        next.push({ slug: slugPath, includeChildren: true });
        return next;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true); setSaveMsg(null);
    // On "default" the partner's own rules are irrelevant — the server
    // deletes them and the partner reads through the project default.
    const ok = await save(mode, mode === "default" ? [] : localRules);
    setSaving(false); setDirty(false);
    if (ok) onChanged();
    setSaveMsg(ok
      ? { ok: true, text: mode === "default"
            ? "This partner now has the project's External view"
            : "Partner access saved" }
      : { ok: false, text: "Failed to save" });
    setTimeout(() => setSaveMsg(null), 3000);
  };
  const handleReset = () => setResetDlg(true);
  // Declared before the header uses it: the mode buttons and Save are dead
  // until the partner's config has actually arrived, or a click on either
  // writes the initial empty config over the real one.
  const isLoading = loading || contentLoading;
  const doReset = async () => { setResetDlg(false); if (await clear()) onChanged(); setDirty(false); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col relative" onClick={e => e.stopPropagation()}>
        {saveMsg && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[9999]">
            <div className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl border text-sm font-medium ${
              saveMsg.ok ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-red-50 border-red-300 text-red-800"
            }`}>
              {saveMsg.ok ? <Check className="size-5 text-emerald-600" /> : <X className="size-5 text-red-600" />}
              {saveMsg.text}
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100" title="Back to projects">
              <ArrowLeft className="size-4" />
            </button>
            <div>
              <div className="text-sm font-semibold text-slate-800">{project.name}</div>
              <div className="text-[12px] text-slate-400">
                {canRead ? "Access for this partner"
                  : !reach || reach.reason === "not_given" ? "This partner can't read it yet. Choose how much, then Give access."
                  : `Can't read: ${describePartnerReach(reach)}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 mr-1">
              {([
                ["default", "External view", "The sections the project's External view shows — the same as every other outside reader."],
                ["default_plus", "External view + extras", "The project's External view, plus anything extra you tick."],
                ["override", "Only what you tick", "Only what you tick. The project's External view is ignored."],
              ] as const).map(([m, label, hint]) => (
                <button key={m} type="button" title={hint} disabled={isLoading}
                  onClick={() => { setMode(m); setDirty(true); }}
                  className={`px-2.5 py-1 rounded-md whitespace-nowrap text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-default ${
                    mode === m ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}>{label}</button>
              ))}
            </div>
            {/* Can't read yet, Save is live without a change: the choice on
                screen IS the change. */}
            <button onClick={handleSave} disabled={saving || (canRead && !dirty) || isLoading}
              className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-[12px] rounded-lg transition-colors ${
                dirty || !canRead ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-slate-100 text-slate-400 cursor-default"
              }`}>
              {saving ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" />} {canRead ? "Save" : "Give access"}
            </button>
            {/* On a Public project everyone outside reads it anyway, so this
                only puts the partner back on the project default. */}
            <button onClick={handleReset} disabled={!hasOwn || isLoading}
              className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-[12px] rounded-lg transition-colors ${
                hasOwn ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-100 text-slate-400 cursor-default"
              }`}>
              <RotateCcw className="size-3" /> {project.audience === "public" ? "Reset" : "Remove access"}
            </button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <div className="py-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <Loader2 className="size-3 animate-spin" /> Loading…
            </div>
          ) : sections.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">No content found for this project.</div>
          ) : (
            <div className="space-y-3">
              {sections.map(sec => {
                const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
                const secState = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
                return (
                  <div key={sec.id}>
                    <div className="flex items-center gap-2 mb-1">
                      <PAccessCheckbox state={secState} locked={mode === "default"}
                        onChange={() => { if (mode !== "default") toggleSection(sec); }} />
                      {sec.sectionType === "api"
                        ? <Code className="size-3.5 text-indigo-500" />
                        : sec.sectionType === "support"
                          ? <LifeBuoy className="size-3.5 text-sky-500" />
                          : sec.sectionType === "faq"
                            ? <HelpCircle className="size-3.5 text-violet-500" />
                            : <FileText className="size-3.5 text-slate-500" />}
                      <span className="text-[12px] font-semibold text-slate-700">{sec.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{sec.sectionType}</span>
                    </div>
                    {docTrees[sec.id]?.map(n => (
                      <PAccessDocNode key={n.id} node={n} depth={1} readonly={mode === "default"}
                        localMode={mode} ruleMap={ruleMap} parentMap={parentMap}
                        isDefaultCovered={() => false} toggleNode={toggleNode} />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-slate-200 text-[11px] text-slate-400 shrink-0">
          {localRules.length} rule{localRules.length !== 1 ? "s" : ""}
        </div>
      </div>
      {resetDlg && (project.audience === "public"
        ? <ConfirmDialog title="Reset this partner's access?" message="Their own settings go. As a Public project, they still read its External view like everyone outside Joveo."
            confirmLabel="Reset" destructive onConfirm={doReset} onCancel={() => setResetDlg(false)} />
        : <ConfirmDialog title="Remove this partner's access?" message="Their people stop seeing this project. Anyone shared with by name keeps their own access."
            confirmLabel="Remove access" destructive onConfirm={doReset} onCancel={() => setResetDlg(false)} />)}
    </div>
  );
}

/* ── Checkbox with locked/readonly state + indeterminate ── */
function PAccessCheckbox({ state, locked, onChange }: { state: CheckState; locked: boolean; onChange: () => void }) {
  return (
    <button onClick={onChange} disabled={locked}
      className={`size-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
        state === "checked"
          ? locked ? "bg-slate-400 border-slate-400 text-white cursor-default" : "bg-indigo-600 border-indigo-600 text-white"
          : state === "partial"
          ? locked ? "bg-slate-400 border-slate-400 text-white cursor-default" : "bg-indigo-600 border-indigo-600 text-white"
          : locked ? "border-slate-200 cursor-default" : "border-slate-300 hover:border-slate-400"
      }`}>
      {state === "checked" && <Check className="size-2.5" />}
      {state === "partial" && <Minus className="size-2.5" />}
    </button>
  );
}

/* ── Doc node row with default awareness + cascading state ── */
function PAccessDocNode({ node, depth, readonly, localMode, ruleMap, parentMap, isDefaultCovered, toggleNode }: {
  node: TreeNode; depth: number; readonly: boolean; localMode: string;
  ruleMap: Map<string, AccessRule>;
  parentMap: Map<string, string | null>;
  isDefaultCovered: (id: string) => boolean;
  toggleNode: (slugPath: string, node: TreeNode) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = (node.children?.length ?? 0) > 0;
  const nodeIsDefault = isDefaultCovered(node.slugPath);
  const locked = readonly || (localMode === "default_plus" && nodeIsDefault);
  const state = getNodeCheckState(node, ruleMap, parentMap);

  return (
    <div>
      <div className={`flex items-center gap-1.5 py-0.5 rounded ${locked ? "" : "hover:bg-slate-50"}`}
        style={{ paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 text-slate-400 hover:text-slate-600">
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : <span className="w-4" />}
        <PAccessCheckbox state={state} locked={locked}
          onChange={() => !locked && toggleNode(node.slugPath, node)} />
        {hasChildren ? <Folder className="size-3 text-amber-500" /> : <FileText className="size-3 text-slate-400" />}
        <span className={`text-[12px] truncate ${locked && state !== "unchecked" ? "text-slate-400" : "text-slate-700"}`}>{node.title}</span>
        {locked && nodeIsDefault && !readonly && (
          <span className="ml-auto text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-400">default</span>
        )}
      </div>
      {expanded && hasChildren && node.children!.map(c => (
        <PAccessDocNode key={c.id} node={c} depth={depth + 1} readonly={readonly}
          localMode={localMode} ruleMap={ruleMap} parentMap={parentMap}
          isDefaultCovered={isDefaultCovered} toggleNode={toggleNode} />
      ))}
    </div>
  );
}



/* ── Local Partner Access Config — used in create flow (no API calls) ── */
// The create-a-partner variant. The partner does not exist yet, so no
// partner-locked project can belong to it — passing "" excludes them all,
// which is what the API would do on save anyway.
function LocalPartnerAccessConfig({ localAccessMap, onAccessChange }: {
  localAccessMap: Record<string, { mode: string; rules: AccessRule[] }>;
  onAccessChange: (m: Record<string, { mode: string; rules: AccessRule[] }>) => void;
}) {
  const [projects, setProjects] = useState<{ id: string; slug: string; name: string }[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [openProject, setOpenProject] = useState<{ id: string; slug: string; name: string } | null>(null);

  useEffect(() => {
    (async () => {
      const res = await authFetch("/api/projects");
      if (res.ok) {
        const data = await res.json();
        // Grantee-aware. supportsNamedExternalAccess alone offered a
        // partner-locked project to the wrong partner, and the API then 422s
        // on save — the admin picks a project, ticks subtrees and only then
        // finds out. The data to filter on is already on the wire.
        setProjects(data.filter((p: any) => supportsPartnerAccess(p, "")));
      }
      setLoadingProjects(false);
    })();
  }, []);

  if (loadingProjects) return <div className="py-3 text-center text-xs text-slate-400">Loading projects…</div>;
  if (!projects.length) return <div className="py-3 text-center text-xs text-slate-400">No external projects found.</div>;

  const modeLabel: Record<string, string> = { default: "External view", default_plus: "External view +", override: "Only what you tick" };

  return (
    <div className="space-y-1">
      {projects.map(p => {
        const cfg = localAccessMap[p.id];
        const configured = !!cfg && cfg.mode !== "default";
        return (
          <button key={p.id} onClick={() => setOpenProject(p)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
              openProject?.id === p.id ? "border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200" : "border-slate-200 hover:border-slate-300 bg-white"
            }`}>
            <Folder className="size-4 text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-slate-800">{p.name}</div>
              <div className="text-[11px] text-slate-400 font-mono">/{p.slug}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {configured ? (
                <>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{modeLabel[cfg.mode] || cfg.mode}</span>
                  {cfg.rules.length > 0 && <span className="text-[10px] text-slate-400">{cfg.rules.length}</span>}
                </>
              ) : (
                <span className="text-[10px] text-slate-300">not configured</span>
              )}
              <ChevronRight className="size-3.5 text-slate-400" />
            </div>
          </button>
        );
      })}
      {openProject && (
        <LocalPartnerProjectModal key={openProject.id} project={openProject}
          initialMode={localAccessMap[openProject.id]?.mode || "default"}
          initialRules={localAccessMap[openProject.id]?.rules || []}
          onSave={(mode, rules) => {
            onAccessChange({ ...localAccessMap, [openProject.id]: { mode, rules } });
            setOpenProject(null);
          }}
          onClose={() => setOpenProject(null)} />
      )}
    </div>
  );
}

/* ── Local Partner Project Modal — override-only tree (no API save) ── */
function LocalPartnerProjectModal({ project, initialRules, onSave, onClose }: {
  project: { id: string; slug: string; name: string };
  initialMode?: string; initialRules: AccessRule[];
  onSave: (mode: string, rules: AccessRule[]) => void;
  onClose: () => void;
}) {
  const [sections, setSections] = useState<DocSection[]>([]);
  const [docTrees, setDocTrees] = useState<Record<string, TreeNode[]>>({});
  const [localRules, setLocalRules] = useState<AccessRule[]>(initialRules);
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setContentLoading(true);
      const secRes = await authFetch(`/api/docs/${project.slug}/sections`);
      if (!secRes.ok) { setContentLoading(false); return; }
      const secs: DocSection[] = await secRes.json();
      setSections(secs);
      const trees: Record<string, TreeNode[]> = {};
      const treeRes = await authFetch(`/api/docs/${project.slug}/tree`);
      const fullTree = treeRes.ok ? (await treeRes.json()).tree || [] : [];
      for (const s of secs) {
        const sectionNode = fullTree.find((n: any) => n.slug === s.slug);
        trees[s.id] = sectionNode?.children || fullTree;
      }
      setDocTrees(trees);
      setContentLoading(false);
    })();
  }, [project.slug]);

  // Build parent map + rule map keyed by slugPath
  const parentMap = new Map<string, string | null>();
  for (const sec of sections) {
    parentMap.set(sec.slug, null);
    buildParentMap(docTrees[sec.id] ?? [], sec.slug, parentMap);
  }
  const ruleMap = buildRuleMap(localRules);

  // Flatten all trees for node lookup
  const allTrees: TreeNode[] = [];
  for (const sec of sections) {
    allTrees.push({ id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] });
  }

  const toggleSection = (sec: DocSection) => {
    const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
    const state = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(sec.slug, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectAllContentSlugPaths(docTrees[sec.id] ?? []));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== sec.slug);
        next.push({ slug: sec.slug, includeChildren: true });
        return next;
      });
    }
  };

  const toggleNode = (slugPath: string, node: TreeNode) => {
    const state = getNodeCheckState(node, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(slugPath, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectDescendantSlugPaths(node));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== slugPath);
        next.push({ slug: slugPath, includeChildren: true });
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] min-h-[50vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><ArrowLeft className="size-4" /></button>
            <div>
              <div className="text-sm font-semibold text-slate-800">{project.name}</div>
              <div className="text-[12px] text-slate-400">Configure doc access</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="size-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {contentLoading ? (
            <div className="py-8 flex items-center justify-center gap-1.5 text-xs text-slate-400"><Loader2 className="size-3 animate-spin" /> Loading…</div>
          ) : sections.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">No content found for this project.</div>
          ) : (
            <div className="space-y-3">
              {sections.map(sec => {
                const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
                const secState = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
                return (
                  <div key={sec.id}>
                    <div className="flex items-center gap-2 mb-1">
                      <PAccessCheckbox state={secState} locked={false} onChange={() => toggleSection(sec)} />
                      {sec.sectionType === "api"
                        ? <Code className="size-3.5 text-indigo-500" />
                        : sec.sectionType === "support"
                          ? <LifeBuoy className="size-3.5 text-sky-500" />
                          : sec.sectionType === "faq"
                            ? <HelpCircle className="size-3.5 text-violet-500" />
                            : <FileText className="size-3.5 text-slate-500" />}
                      <span className="text-[12px] font-semibold text-slate-700">{sec.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{sec.sectionType}</span>
                    </div>
                    {docTrees[sec.id]?.map(n => (
                      <LPDocNode key={n.id} node={n} depth={1} readonly={false} ruleMap={ruleMap} parentMap={parentMap} toggleNode={toggleNode} />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between shrink-0">
          <span className="text-[11px] text-slate-400">{localRules.length} rule{localRules.length !== 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
            <button onClick={() => onSave("override", localRules)}
              className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Local doc node (simplified — no defaults) ── */
function LPDocNode({ node, depth, readonly, ruleMap, parentMap, toggleNode }: {
  node: TreeNode; depth: number; readonly: boolean;
  ruleMap: Map<string, AccessRule>;
  parentMap: Map<string, string | null>;
  toggleNode: (slugPath: string, node: TreeNode) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = (node.children?.length ?? 0) > 0;
  const state = getNodeCheckState(node, ruleMap, parentMap);

  return (
    <div>
      <div className={`flex items-center gap-1.5 py-0.5 rounded ${readonly ? "" : "hover:bg-slate-50"}`}
        style={{ paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 text-slate-400 hover:text-slate-600">
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : <span className="w-4" />}
        <PAccessCheckbox state={state} locked={readonly} onChange={() => !readonly && toggleNode(node.slugPath, node)} />
        {hasChildren ? <Folder className="size-3 text-amber-500" /> : <FileText className="size-3 text-slate-400" />}
        <span className="text-[12px] truncate text-slate-700">{node.title}</span>
      </div>
      {expanded && hasChildren && node.children!.map(c => (
        <LPDocNode key={c.id} node={c} depth={depth + 1} readonly={readonly} ruleMap={ruleMap} parentMap={parentMap} toggleNode={toggleNode} />
      ))}
    </div>
  );
}
